import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ReactFlow, { Controls, Handle, Position, useNodesState, useEdgesState } from 'reactflow';
import type { Node, Edge } from 'reactflow';
import 'reactflow/dist/style.css';
import * as LucideIcons from 'lucide-react';
import { ArrowLeft, Save, Play, Plus, Trash2, CheckCircle2, XCircle, HelpCircle, Pencil, Zap, FolderPlus } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { automationApi, withClientIdParams } from '../../services/automationapi';
import type { CatalogData, LookupsData } from '../../services/automationapi';
import toast from 'react-hot-toast';
import { CustomSelect, type SelectOption } from './components/CustomSelect';
import { ALL_ASSET_TYPE_OPTIONS } from './constants';
import { useClientLevel } from '../../hooks/useClientLevel';
import { TriggerNodeStatsBadges, ActionNodeStatsBadges } from './components/RuleNodeStats';
import SingleSelectLocalSearchDropdown from '../../components/SingleSelectLocalSearchDropdown';
import { WHATSAPP_LANGUAGES } from '../Settings/Integrations/WhatsappIntegrationModal';

const toPascalCase = (str: string) => {
  if (!str) return '';
  return str
    .replace(/[-_](.)/g, (_, c) => c.toUpperCase())
    .replace(/^(.)/, (_, c) => c.toUpperCase());
};

const getLucideIcon = (iconName?: string): React.ComponentType<{ className?: string }> => {
  if (!iconName) return LucideIcons.Box;

  if ((LucideIcons as any)[iconName]) {
    return (LucideIcons as any)[iconName];
  }

  const pascalName = iconName
    .split(/[-_\s]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  if ((LucideIcons as any)[pascalName]) {
    return (LucideIcons as any)[pascalName];
  }

  return LucideIcons.Box;
};

// Helper to get trigger icon
const getTriggerIcon = (source: string, iconName?: string, className?: string) => {
  const iconClasses = className || "w-8 h-8 text-orange-400";

  // Check ALL_ASSET_TYPE_OPTIONS from constants.ts first
  const assetOpt = ALL_ASSET_TYPE_OPTIONS.find(o => o.value === source || o.value === iconName || o.label.toLowerCase() === source?.toLowerCase());
  if (assetOpt && assetOpt.icon) {
    const IconComp = assetOpt.icon;
    return <IconComp className={iconClasses} />;
  }

  const nameToTry = iconName || source;
  if (nameToTry) {
    const pascalName = toPascalCase(nameToTry);
    const IconComp = (LucideIcons as any)[nameToTry] || (LucideIcons as any)[pascalName];
    if (IconComp && (typeof IconComp === 'object' || typeof IconComp === 'function')) {
      return <IconComp className={iconClasses} />;
    }
  }

  switch (source) {
    case 'switch_ports':
    case 'switch_port':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-ethernet-port-icon lucide-ethernet-port ${iconClasses}`}><path d="M10 8v1" /><path d="M14 8v1" /><path d="M18 8v1" /><path d="M19 17a2 2 0 00-1.765 1.059l-.47.882A2 2 0 0115 20H9a2 2 0 01-1.765-1.059l-.47-.882A2 2 0 005 17H4a2 2 0 01-2-2V6a2 2 0 012-2h16a2 2 0 012 2v9a2 2 0 01-2 2z" /><path d="M6 8v1" /></svg>
      );
    case 'tls_certificate':
    case 'certificates':
      return (
        <svg className={iconClasses} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          <path d="M12 15v3" />
        </svg>
      );
    case 'assets':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`lucide lucide-router-icon lucide-router ${iconClasses}`}><rect width="20" height="8" x="2" y="14" rx="2" /><path d="M6.01 18H6" /><path d="M10.01 18H10" /><path d="M15 10v4" /><path d="M17.84 7.17a4 4 0 0 0-5.66 0" /><path d="M20.66 4.34a8 8 0 0 0-11.31 0" /></svg>
      );
    case 'ad_users':
    case 'active_directory':
      return (
        <svg className={iconClasses} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'event_types':
      return (
        <svg className={iconClasses} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case 'applications':
    case 'apps':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${iconClasses} lucide lucide-box-icon lucide-box`}><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
      );
    default:
      return (
        <svg className={iconClasses} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
  }
};

const getActionIcon = (actionType: string, className: string = "w-10 h-10") => {
  switch (actionType) {
    case 'whatsapp':
      return (
        <img src="/whatsapp.png" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="WhatsApp" />
      );
    case 'slack':
      return (
        <svg className={`${className} shrink-0`} viewBox="0 0 24 24" fill="none">
          <path d="M5.04 15.12a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52zM6.3 15.12a2.52 2.52 0 0 1 5.04 0v5.04a2.52 2.52 0 1 1-5.04 0v-5.04z" fill="#36C5F0" />
          <path d="M8.88 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.88zM8.88 6.3a2.52 2.52 0 0 1 0 5.04H3.84a2.52 2.52 0 1 1 0-5.04h5.04z" fill="#2EB67D" />
          <path d="M18.96 8.88a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.88zM17.7 8.88a2.52 2.52 0 0 1-5.04 0V3.84a2.52 2.52 0 1 1 5.04 0v5.04z" fill="#ECB22E" />
          <path d="M15.12 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52zM15.12 17.7a2.52 2.52 0 0 1 0-5.04h5.04a2.52 2.52 0 1 1 0 5.04h-5.04z" fill="#E01E5A" />
        </svg>
      );
    case 'email':
      return (
        <img src="/email_workflow.png" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="Email" />
      );
    case 'webhook':
      return (
        <img src="/webhook.svg" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="Webhook" />
      );
    case 'teams':
      return (
        <img src="/teams.png" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="Teams" />
      );
    case 'sms':
      return (
        <img src="/sms.png" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="SMS" />
      );
    case 'telegram':
      return (
        <img src="/telegram.png" className={`${className} rounded object-cover select-none pointer-events-none shrink-0`} alt="Telegram" />
      );
    case 'script':
      return (
        <svg className={`${className} text-orange-400 shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </svg>
      );
    case 'delay':
      return (
        <svg className={`${className} text-blue-400 shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'alert':
      return (
        <svg className={`${className} text-amber-400 shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'ticket':
    case 'ticketing':
      return <img src="/rdp_logo.png" className={`${className} object-contain select-none pointer-events-none shrink-0`} alt="Ticketing" />;
    case 'event':
      return (
        <svg className={`${className} text-emerald-400 shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    default:
      return (
        <svg className={`${className} text-teal-400 shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      );
  }
};

const AssetTypeNode = ({ data }: any) => (
  <div className="flex flex-col items-center group cursor-pointer">
    <div className={`w-16 h-16 rounded-2xl bg-gray-900 border ${data.selected ? 'ring-2 ring-blue-500 border-transparent' : 'border-gray-800 hover:border-blue-500'} transition-all flex items-center justify-center shadow-lg group-hover:scale-105 duration-150 relative`}>
      {getTriggerIcon(data.targetAssetType, undefined, "w-8 h-8 text-blue-400")}
      <Handle type="source" position={Position.Right} style={{ background: '#3b82f6', width: 6, height: 6, right: -3 }} />
    </div>
    <div className="mt-1.5 text-[10px] font-bold text-gray-200 group-hover:text-white transition-colors text-center w-28 truncate select-none leading-none">
      {data.label}
    </div>
    <div className="mt-1 text-[8px] font-semibold text-blue-400 text-center select-none bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded-full">
      Asset Type
    </div>
  </div>
);

const TriggerNode = ({ data }: any) => (
  <div className="flex flex-col items-center group cursor-pointer">
    {/* Icon Wrapper */}
    <div className={`w-16 h-16 rounded-2xl bg-gray-900 border ${data.selected ? 'ring-2 ring-orange-500 border-transparent' : 'border-gray-800 hover:border-orange-500'} transition-all flex items-center justify-center shadow-lg group-hover:scale-105 duration-150 relative`}>
      <Handle type="target" position={Position.Left} style={{ background: '#3b82f6', width: 6, height: 6, left: -3 }} />
      {getTriggerIcon(data.triggerSource || data.targetAssetType, data.icon)}

      {/* Handle with hover plus/open button */}
      <div className="absolute right-[-6px] top-[29px] w-3.5 h-3.5 flex items-center justify-center">
        <Handle type="source" position={Position.Right} style={{ background: '#f97316', width: 6, height: 6, right: 0 }} />
        {data.onAddCondition && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              data.onAddCondition();
            }}
            className="absolute opacity-0 group-hover:opacity-100 w-4 h-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-bold flex items-center justify-center border border-gray-950 hover:scale-110 active:scale-95 transition-all shadow-md cursor-pointer z-20"
            style={{ right: -5 }}
            title={data.hasCondition ? "Open Condition Configuration" : "Add Condition"}
          >
            {data.hasCondition ? "→" : "+"}
          </button>
        )}
      </div>
    </div>
    {/* Label underneath */}
    <div className="mt-1.5 text-[10px] font-bold text-gray-200 group-hover:text-white transition-colors text-center w-28 truncate select-none leading-none">
      {data.label}
    </div>
    <div className="mt-1 text-[8px] font-semibold text-orange-400 text-center select-none bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded-full">
      {data.triggerSource ? "Collection" : "Select Collection →"}
    </div>

    {/* Stats Badges */}
    <TriggerNodeStatsBadges stats={data.stats} />
  </div>
);


const getItemFields = (fieldObj: any): any[] => {
  if (!fieldObj) return [];
  return fieldObj.item_fields || fieldObj.itemFields || fieldObj.sub_fields || [];
};

const parseFieldPath = (fieldStr: string, colFields: any[]) => {
  if (!fieldStr || !colFields || colFields.length === 0) {
    return { topKey: '', subKey: '', topField: null, subField: null };
  }

  // 1. Check exact match
  const exactMatch = colFields.find((f: any) => (f.key || f.path) === fieldStr);
  if (exactMatch) {
    const itemFields = getItemFields(exactMatch);
    if (itemFields.length === 0) {
      return { topKey: fieldStr, subKey: '', topField: exactMatch, subField: null };
    }
  }

  // 2. Check prefix matches (topKey + '.')
  // Sort candidates by key length descending so longer top keys (e.g. "results.domainInfo") match first
  const candidates = colFields
    .map((f: any) => {
      const key = f.key || f.path;
      return { topField: f, topKey: key, itemFields: getItemFields(f) };
    })
    .filter((item) => item.topKey && fieldStr.startsWith(item.topKey + '.'))
    .sort((a, b) => b.topKey.length - a.topKey.length);

  for (const candidate of candidates) {
    const remainder = fieldStr.slice(candidate.topKey.length + 1);
    const subMatch = candidate.itemFields.find((sub: any) => (sub.key || sub.path) === remainder);
    if (subMatch || candidate.itemFields.length > 0) {
      return {
        topKey: candidate.topKey,
        subKey: remainder,
        topField: candidate.topField,
        subField: subMatch || null,
      };
    }
  }

  return { topKey: fieldStr, subKey: '', topField: exactMatch || null, subField: null };
};

const getDefaultFieldPath = (colFields: any[]): string => {
  if (!colFields || colFields.length === 0) return '';
  const firstCol = colFields[0];
  const topKey = firstCol.key || firstCol.path || '';
  const items = getItemFields(firstCol);
  if (items.length > 0) {
    const subKey = items[0].key || items[0].path || '';
    return subKey ? `${topKey}.${subKey}` : topKey;
  }
  return topKey;
};

const getOperatorsForType = (ruleEngineOperators: any, ruleEngineCollections: any, fieldType: string): any[] => {
  const typeToUse = (fieldType || 'string').toLowerCase();
  const opsObj = ruleEngineOperators?.operators || ruleEngineOperators;

  let foundOps: any[] = [];

  if (opsObj && typeof opsObj === 'object' && !Array.isArray(opsObj)) {
    if (Array.isArray(opsObj[typeToUse])) {
      foundOps = opsObj[typeToUse];
    } else if (typeToUse === 'int' || typeToUse === 'integer' || typeToUse === 'float' || typeToUse === 'double' || typeToUse === 'number') {
      if (Array.isArray(opsObj['int'])) foundOps = opsObj['int'];
      else if (Array.isArray(opsObj['number'])) foundOps = opsObj['number'];
      else if (Array.isArray(opsObj['integer'])) foundOps = opsObj['integer'];
    } else if (typeToUse === 'bool' || typeToUse === 'boolean') {
      if (Array.isArray(opsObj['bool'])) foundOps = opsObj['bool'];
      else if (Array.isArray(opsObj['boolean'])) foundOps = opsObj['boolean'];
    } else if (typeToUse === 'date' || typeToUse === 'datetime' || typeToUse === 'timestamp' || typeToUse === 'datetime-local') {
      if (Array.isArray(opsObj[typeToUse])) foundOps = opsObj[typeToUse];
      else if (Array.isArray(opsObj['datetime'])) foundOps = opsObj['datetime'];
      else if (Array.isArray(opsObj['date'])) foundOps = opsObj['date'];
      else if (Array.isArray(opsObj['timestamp'])) foundOps = opsObj['timestamp'];
    }
  }

  if (foundOps.length === 0 && Array.isArray(opsObj)) {
    const filtered = opsObj.filter((o: any) => {
      const oType = (o.type || '').toLowerCase();
      const appTypes = (o.applicable_types || []).map((t: string) => String(t).toLowerCase());
      return !o.type || oType === typeToUse || appTypes.includes(typeToUse);
    });
    if (filtered.length > 0) foundOps = filtered;
  }

  if (foundOps.length === 0 && ruleEngineCollections?.operators && typeof ruleEngineCollections.operators === 'object') {
    if (Array.isArray(ruleEngineCollections.operators[typeToUse])) {
      foundOps = ruleEngineCollections.operators[typeToUse];
    } else if (typeToUse === 'int' || typeToUse === 'integer' || typeToUse === 'float' || typeToUse === 'double' || typeToUse === 'number') {
      if (Array.isArray(ruleEngineCollections.operators['int'])) foundOps = ruleEngineCollections.operators['int'];
      else if (Array.isArray(ruleEngineCollections.operators['number'])) foundOps = ruleEngineCollections.operators['number'];
      else if (Array.isArray(ruleEngineCollections.operators['integer'])) foundOps = ruleEngineCollections.operators['integer'];
    } else if (typeToUse === 'bool' || typeToUse === 'boolean') {
      if (Array.isArray(ruleEngineCollections.operators['bool'])) foundOps = ruleEngineCollections.operators['bool'];
      else if (Array.isArray(ruleEngineCollections.operators['boolean'])) foundOps = ruleEngineCollections.operators['boolean'];
    } else if (typeToUse === 'date' || typeToUse === 'datetime' || typeToUse === 'timestamp' || typeToUse === 'datetime-local') {
      if (Array.isArray(ruleEngineCollections.operators[typeToUse])) foundOps = ruleEngineCollections.operators[typeToUse];
      else if (Array.isArray(ruleEngineCollections.operators['datetime'])) foundOps = ruleEngineCollections.operators['datetime'];
      else if (Array.isArray(ruleEngineCollections.operators['date'])) foundOps = ruleEngineCollections.operators['date'];
      else if (Array.isArray(ruleEngineCollections.operators['timestamp'])) foundOps = ruleEngineCollections.operators['timestamp'];
    }
  }

  if (foundOps.length === 0 && opsObj && typeof opsObj === 'object' && !Array.isArray(opsObj)) {
    if (Array.isArray(opsObj['string'])) foundOps = opsObj['string'];
    else {
      const keys = Object.keys(opsObj);
      if (keys.length > 0 && Array.isArray(opsObj[keys[0]])) {
        foundOps = opsObj[keys[0]];
      }
    }
  }

  // Fallback default operators for date/datetime if API returns empty
  if (foundOps.length === 0 && (typeToUse === 'date' || typeToUse === 'datetime' || typeToUse === 'timestamp' || typeToUse === 'datetime-local')) {
    foundOps = [
      { key: 'equals', value: 'equals', label: 'Equals' },
      { key: 'not_equals', value: 'not_equals', label: 'Not Equals' },
      { key: 'greater_than', value: 'greater_than', label: 'After / Greater Than (>)' },
      { key: 'less_than', value: 'less_than', label: 'Before / Less Than (<)' },
      { key: 'greater_than_or_equal', value: 'greater_than_or_equal', label: 'On or After (>=)' },
      { key: 'less_than_or_equal', value: 'less_than_or_equal', label: 'On or Before (<=)' },
      { key: 'is_null', value: 'is_null', label: 'Is Empty / Null', requires_value: false },
      { key: 'is_not_null', value: 'is_not_null', label: 'Is Not Empty / Not Null', requires_value: false },
    ];
  }

  return foundOps;
};

export interface ConditionGroupNode {
  id?: string;
  operator?: 'AND' | 'OR';
  groupLogic?: 'AND' | 'OR';
  rules: Array<ConditionRuleItem | ConditionGroupNode>;
}

const isConditionRow = (node: any): node is ConditionRuleItem => {
  return node && typeof node === 'object' && ('field' in node);
};

function updateAt(tree: ConditionGroupNode, path: number[], fn: (node: any) => any): ConditionGroupNode {
  if (path.length === 0) return fn(tree);
  const [i, ...rest] = path;
  const rules = (tree.rules || []).slice();
  rules[i] = updateAt(rules[i] as ConditionGroupNode, rest, fn);
  return { ...tree, rules };
}

function removeAt(tree: ConditionGroupNode, path: number[]): ConditionGroupNode {
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return updateAt(tree, parentPath, (n) => ({
    ...n,
    rules: (n.rules || []).filter((_: any, i: number) => i !== idx)
  }));
}

const parsePossibleTimestampOrDate = (val: any): Date | null => {
  if (val === undefined || val === null || val === '') return null;
  if (val instanceof Date && !isNaN(val.getTime())) return val;

  // Check if it's a numeric timestamp (number or numeric string)
  if (typeof val === 'number' || (/^\d+$/.test(String(val).trim()) && !String(val).includes('-'))) {
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      // If seconds (e.g. 1817078400, < 10000000000), multiply by 1000
      const ms = num < 10000000000 ? num * 1000 : num;
      const d = new Date(ms);
      if (!isNaN(d.getTime()) && d.getFullYear() > 1970 && d.getFullYear() < 2100) {
        return d;
      }
    }
  }

  // Check if it's an ISO or date string
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.includes('-') || trimmed.includes('/') || trimmed.includes('T')) {
      const d = new Date(trimmed);
      if (!isNaN(d.getTime()) && d.getFullYear() > 1970 && d.getFullYear() < 2100) {
        return d;
      }
    }
  }

  return null;
};

const formatConditionDisplayValue = (
  val: any,
  fieldKey?: string,
  opKey?: string,
  fieldType?: string
): string => {
  if (val === undefined || val === null || val === '') return '';

  const rawField = String(fieldKey || '').toLowerCase();
  const rawOp = String(opKey || '').toLowerCase();
  const rawType = String(fieldType || '').toLowerCase().trim();

  const isDateContext =
    rawType === 'date' ||
    rawType === 'datetime' ||
    rawType === 'timestamp' ||
    rawField.includes('date') ||
    rawField.includes('time') ||
    rawField.includes('after') ||
    rawField.includes('before') ||
    rawField.includes('created') ||
    rawField.includes('updated') ||
    rawField.includes('expires') ||
    rawField.includes('valid_') ||
    rawOp.includes('after') ||
    rawOp.includes('before');

  const parsedDate = parsePossibleTimestampOrDate(val);
  if (parsedDate && (isDateContext || typeof val === 'number')) {
    const pad = (n: number) => String(n).padStart(2, '0');
    const yyyy = parsedDate.getFullYear();
    const mm = pad(parsedDate.getMonth() + 1);
    const dd = pad(parsedDate.getDate());
    const hh = pad(parsedDate.getHours());
    const min = pad(parsedDate.getMinutes());

    if (rawType === 'date') {
      return `${yyyy}-${mm}-${dd}`;
    }

    if (rawType === 'datetime' || rawType === 'timestamp') {
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    }

    // If type key is not coming / undefined, show date and time
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  }

  return String(val);
};

const ConditionRowView: React.FC<{
  node: ConditionRuleItem;
  path: number[];
  triggerSource: string;
  ruleEngineCollections: any;
  ruleEngineOperators: any;
  onChangeTree: (path: number[], fn: (n: any) => any) => void;
  onRemove: () => void;
  isViewMode: boolean;
}> = ({ node, path, triggerSource, ruleEngineCollections, ruleEngineOperators, onChangeTree, onRemove, isViewMode }) => {
  const collectionsList = ruleEngineCollections?.collections || [];
  const currCol = collectionsList.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
  const colFields: any[] = currCol?.fields || [];

  const topFieldOptions = colFields.map((f: any) => ({
    value: f.key || f.path,
    label: f.label || f.key || f.path,
  }));

  const parsedField = useMemo(() => {
    return parseFieldPath(node.field || '', colFields);
  }, [node.field, colFields]);

  const selectedTopKey = parsedField.topKey;
  const selectedSubKey = parsedField.subKey;
  const selectedTopField = parsedField.topField;
  const itemFields = getItemFields(selectedTopField);
  const hasSubFields = itemFields.length > 0;

  const subFieldOptions = itemFields.map((sub: any) => ({
    value: sub.key || sub.path,
    label: sub.label || sub.key || sub.path,
  }));

  const selectedSubFieldObj = parsedField.subField;
  const rawType = node.type || selectedSubFieldObj?.type || (hasSubFields ? itemFields[0]?.type : selectedTopField?.type) || 'string';
  const fieldType = String(rawType).toLowerCase();

  const opsForType = getOperatorsForType(ruleEngineOperators, ruleEngineCollections, fieldType);
  const opOptions = opsForType.map((op: any) => ({
    value: op.key || op.value,
    label: op.label || op.key || op.value,
  }));

  const currentOp = node.op || node.operator || (opsForType[0]?.key || opsForType[0]?.value || '');
  const currentOpDef = opsForType.find((o: any) => (o.key || o.value) === currentOp) || opsForType[0];
  const requiresValue = currentOpDef ? currentOpDef.requires_value !== false : true;

  const isDate = fieldType === 'date';
  const isDateTime = ['datetime', 'datetime-local', 'timestamp'].includes(fieldType);
  const isNumeric = ['int', 'integer', 'float', 'double', 'number'].includes(fieldType);

  let inputType = 'text';
  let placeholderText = requiresValue ? "Value (Required)" : "No value required";

  if (isNumeric) {
    inputType = 'number';
    placeholderText = requiresValue ? "Enter number" : "No value required";
  } else if (isDate) {
    inputType = 'date';
    placeholderText = requiresValue ? "Select date" : "No value required";
  } else if (isDateTime) {
    inputType = 'datetime-local';
    placeholderText = requiresValue ? "Select date & time" : "No value required";
  }

  const formatInputValue = (val: any) => {
    if (val === undefined || val === null || val === '') return '';
    if (isDate || isDateTime) {
      const parsed = parsePossibleTimestampOrDate(val);
      if (parsed) {
        const pad = (n: number) => String(n).padStart(2, '0');
        const yyyy = parsed.getFullYear();
        const mm = pad(parsed.getMonth() + 1);
        const dd = pad(parsed.getDate());
        if (isDate) {
          return `${yyyy}-${mm}-${dd}`;
        }
        if (isDateTime) {
          const hh = pad(parsed.getHours());
          const min = pad(parsed.getMinutes());
          return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
        }
      }
    }
    const str = String(val);
    if (isDate) {
      if (str.includes('T')) return str.split('T')[0];
      return str.slice(0, 10);
    }
    if (isDateTime) {
      if (str.includes('Z')) return str.replace('Z', '').slice(0, 16);
      if (str.includes(' ')) return str.replace(' ', 'T').slice(0, 16);
      if (str.includes('T')) return str.slice(0, 16);
      return str;
    }
    return val;
  };

  const handleTopFieldChange = (newTopKey: string) => {
    const targetTopField = colFields.find((f: any) => (f.key || f.path) === newTopKey);
    const targetItemFields = getItemFields(targetTopField);

    let newFieldPath = newTopKey;
    let newType = targetTopField?.type || 'string';

    if (targetItemFields.length > 0) {
      const firstSubKey = targetItemFields[0]?.key || targetItemFields[0]?.path || '';
      if (firstSubKey) {
        newFieldPath = `${newTopKey}.${firstSubKey}`;
        newType = targetItemFields[0]?.type || 'string';
      }
    }

    const newOps = getOperatorsForType(ruleEngineOperators, ruleEngineCollections, newType);
    const defaultOp = newOps[0]?.key || newOps[0]?.value || '';
    onChangeTree(path, (n) => ({ ...n, field: newFieldPath, op: defaultOp, operator: defaultOp, value: '', type: newType }));
  };

  const handleSubFieldChange = (newSubKey: string) => {
    if (!selectedTopKey) return;
    const newFieldPath = `${selectedTopKey}.${newSubKey}`;
    const targetSubField = itemFields.find((sub: any) => (sub.key || sub.path) === newSubKey);
    const newType = targetSubField?.type || 'string';

    const newOps = getOperatorsForType(ruleEngineOperators, ruleEngineCollections, newType);
    const currentOpVal = node.op || node.operator || '';
    const opStillValid = newOps.some((o: any) => (o.key || o.value) === currentOpVal);
    const defaultOp = opStillValid ? currentOpVal : (newOps[0]?.key || newOps[0]?.value || '');

    onChangeTree(path, (n) => ({
      ...n,
      field: newFieldPath,
      op: defaultOp,
      operator: defaultOp,
      value: opStillValid ? (n.value ?? '') : '',
      type: newType
    }));
  };

  const setOperator = (val: string) => {
    const opObj = opsForType.find((o: any) => (o.key || o.value) === val);
    const reqVal = opObj?.requires_value !== false;
    onChangeTree(path, (n) => ({ ...n, op: val, operator: val, value: reqVal ? (n.value ?? '') : undefined }));
  };

  const setValue = (val: any) => {
    onChangeTree(path, (n) => ({ ...n, value: val }));
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-2.5 space-y-2 relative shadow-sm">
      {!isViewMode && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 p-1 text-gray-200 hover:text-red-400 hover:bg-red-950/20 rounded transition-colors cursor-pointer z-10"
          title="Remove Condition"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Field Path Selection */}
      <div className="space-y-2 pr-6">
        {hasSubFields ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[9px] text-gray-200 uppercase tracking-wider font-bold">Field Path</label>
              <CustomSelect
                value={selectedTopKey}
                onChange={handleTopFieldChange}
                disabled={isViewMode}
                placeholder="Select Field Path..."
                className="w-full font-sans"
                options={topFieldOptions}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-gray-200 uppercase tracking-wider font-bold">Sub Field Path</label>
              <CustomSelect
                value={selectedSubKey}
                onChange={handleSubFieldChange}
                disabled={isViewMode}
                placeholder="Select Sub Field..."
                className="w-full font-sans"
                options={subFieldOptions}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <label className="text-[9px] text-gray-200 uppercase tracking-wider font-bold">Field Path</label>
            <CustomSelect
              value={selectedTopKey}
              onChange={handleTopFieldChange}
              disabled={isViewMode}
              placeholder="Select Field Path..."
              className="w-full font-sans"
              options={topFieldOptions}
            />
          </div>
        )}
      </div>

      {/* Operator & Value */}
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-[9px] text-gray-200 uppercase tracking-wider font-bold">Operator</label>
          <CustomSelect
            value={currentOp}
            onChange={setOperator}
            disabled={isViewMode}
            className="w-full font-sans"
            options={opOptions}
          />
        </div>

        <div className="space-y-1">
          <label className="text-[9px] uppercase tracking-wider font-bold text-gray-200">
            Value {requiresValue ? <span className="text-red-500">*</span> : <span className="text-gray-200 font-normal">(-)</span>}
          </label>
          <input
            type={inputType}
            value={requiresValue ? formatInputValue(node.value) : ''}
            onChange={e => setValue(e.target.value)}
            placeholder={placeholderText}
            onClick={(e) => {
              if (!isViewMode && requiresValue && (isDate || isDateTime) && (e.currentTarget as any).showPicker) {
                try {
                  (e.currentTarget as any).showPicker();
                } catch { }
              }
            }}
            disabled={isViewMode || !requiresValue}
            className={`w-full bg-gray-950 border rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-indigo-500 font-mono text-[11px] ${
              (isDate || isDateTime) && !isViewMode && requiresValue ? 'cursor-pointer' : ''
            } ${!requiresValue
              ? 'opacity-40 cursor-not-allowed border-gray-800/50 bg-gray-950/50 text-gray-200 select-none'
              : 'border-gray-800 hover:border-gray-700'
              }`}
          />
        </div>
      </div>
    </div>
  );
};

const GroupNodeView: React.FC<{
  node: ConditionGroupNode;
  path: number[];
  collectionKey: string;
  ruleEngineCollections: any;
  ruleEngineOperators: any;
  onChangeTree: (path: number[], fn: (n: any) => any) => void;
  onRemove: (() => void) | null;
  depth: number;
  isViewMode: boolean;
}> = ({ node, path, collectionKey, ruleEngineCollections, ruleEngineOperators, onChangeTree, onRemove, depth, isViewMode }) => {
  const currentOp = node.operator || node.groupLogic || 'AND';

  const toggleOperator = () => {
    const nextOp = currentOp === 'OR' ? 'AND' : 'OR';
    onChangeTree(path, (n) => ({ ...n, operator: nextOp, groupLogic: nextOp }));
  };

  const addRule = () => {
    const collectionsList = ruleEngineCollections?.collections || [];
    const currCol = collectionsList.find((c: any) => c.key === collectionKey || c.db_table === collectionKey);
    const defaultField = getDefaultFieldPath(currCol?.fields || []);
    const parsed = parseFieldPath(defaultField, currCol?.fields || []);
    const fieldObj = parsed.subField || parsed.topField;
    const defaultType = fieldObj?.type || 'string';
    const newCond: ConditionRuleItem = {
      id: `r-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      field: defaultField,
      op: 'equals',
      operator: 'equals',
      value: '',
      type: defaultType
    };
    onChangeTree(path, (n) => ({ ...n, rules: [...(n.rules || []), newCond] }));
  };

  const addGroup = () => {
    const collectionsList = ruleEngineCollections?.collections || [];
    const currCol = collectionsList.find((c: any) => c.key === collectionKey || c.db_table === collectionKey);
    const defaultField = getDefaultFieldPath(currCol?.fields || []);
    const parsed = parseFieldPath(defaultField, currCol?.fields || []);
    const fieldObj = parsed.subField || parsed.topField;
    const defaultType = fieldObj?.type || 'string';
    const initialRule: ConditionRuleItem = {
      id: `r-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      field: defaultField,
      op: 'equals',
      operator: 'equals',
      value: '',
      type: defaultType
    };
    const newGrp: ConditionGroupNode = {
      id: `g-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      operator: 'AND',
      groupLogic: 'AND',
      rules: [initialRule]
    };
    onChangeTree(path, (n) => ({ ...n, rules: [...(n.rules || []), newGrp] }));
  };

  const bgClass = depth === 0 ? 'bg-gray-850 border-indigo-500/30' : (depth % 2 === 1 ? 'bg-gray-900 border-gray-800' : 'bg-gray-950 border-indigo-500/20');

  return (
    <div className={`rounded-xl border ${bgClass} p-3.5 space-y-3 relative shadow-lg`}>
      {/* Group Header */}
      <div className="flex items-center justify-between border-b border-gray-800 pb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={isViewMode}
            onClick={toggleOperator}
            className={`px-2.5 py-0.5 text-[9px] font-extrabold uppercase rounded-full border transition-all cursor-pointer shadow-sm ${currentOp === 'OR'
              ? 'bg-amber-500/20 text-amber-300 border-amber-500/60 hover:bg-amber-500/30'
              : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/60 hover:bg-indigo-500/30'
              }`}
          >
            {currentOp}
          </button>
          <span className="text-[10px] text-gray-200 font-bold uppercase tracking-wider">
            {depth === 0 ? 'Root Group' : `Group Level ${depth}`} ({node.rules?.length || 0} item{(node.rules?.length || 0) !== 1 ? 's' : ''})
          </span>
        </div>

        {!isViewMode && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-gray-200 hover:text-red-400 hover:bg-red-950/20 rounded transition-colors cursor-pointer"
            title="Delete Group"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Rules & Sub-Groups */}
      <div className="space-y-2.5">
        {(!node.rules || node.rules.length === 0) ? (
          <div className="text-xs text-gray-200 italic p-3 text-center border border-dashed border-gray-800 rounded-lg">
            No items in this group. Click + Condition or + Group below.
          </div>
        ) : (
          node.rules.map((child, i) => (
            <React.Fragment key={(child as any).id || i}>
              {i > 0 && (
                <div className="flex items-center justify-center my-1">
                  <span className="text-[8px] font-black uppercase px-2 py-0.5 bg-gray-950 text-indigo-300 border border-gray-800 rounded-full select-none">
                    {currentOp}
                  </span>
                </div>
              )}
              {isConditionRow(child) ? (
                <ConditionRowView
                  node={child}
                  path={[...path, i]}
                  triggerSource={collectionKey}
                  ruleEngineCollections={ruleEngineCollections}
                  ruleEngineOperators={ruleEngineOperators}
                  onChangeTree={onChangeTree}
                  onRemove={() => onChangeTree([], (t) => removeAt(t, [...path, i]))}
                  isViewMode={isViewMode}
                />
              ) : (
                <GroupNodeView
                  node={child as ConditionGroupNode}
                  path={[...path, i]}
                  collectionKey={collectionKey}
                  ruleEngineCollections={ruleEngineCollections}
                  ruleEngineOperators={ruleEngineOperators}
                  onChangeTree={onChangeTree}
                  onRemove={() => onChangeTree([], (t) => removeAt(t, [...path, i]))}
                  depth={depth + 1}
                  isViewMode={isViewMode}
                />
              )}
            </React.Fragment>
          ))
        )}

        {/* Action Buttons: Add Condition & Add Group */}
        {!isViewMode && (
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={addRule}
              className="flex-1 py-1.5 text-[10px] font-bold text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 rounded-lg flex items-center justify-center gap-1 transition-all cursor-pointer"
            >
              <Plus className="w-3 h-3" /> CONDITION
            </button>
            <button
              type="button"
              onClick={addGroup}
              className="flex-1 py-1.5 text-[10px] font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg flex items-center justify-center gap-1 transition-all cursor-pointer"
            >
              <FolderPlus className="w-3 h-3" /> GROUP
            </button>
          </div>
        )}
      </div>
    </div>
  );
};



const isOperatorWithoutValue = (opKey?: string, ruleEngineOperators?: any): boolean => {
  if (!opKey) return false;
  const normalizedOp = String(opKey).toLowerCase().trim();

  const noValueOps = [
    'is_true', 'is_false', 'is_null', 'is_not_null', 'is_empty', 'is_not_empty',
    'exists', 'not_exists', 'is_blank', 'is_not_blank', 'true', 'false', 'null', 'not_null'
  ];
  if (noValueOps.includes(normalizedOp)) return true;

  if (ruleEngineOperators) {
    const opsObj = ruleEngineOperators?.operators || ruleEngineOperators;
    if (Array.isArray(opsObj)) {
      const match = opsObj.find((o: any) => (o.key || o.value || '').toLowerCase() === normalizedOp);
      if (match && match.requires_value === false) return true;
    } else if (typeof opsObj === 'object') {
      for (const key of Object.keys(opsObj)) {
        if (Array.isArray(opsObj[key])) {
          const match = opsObj[key].find((o: any) => (o.key || o.value || '').toLowerCase() === normalizedOp);
          if (match && match.requires_value === false) return true;
        }
      }
    }
  }

  return false;
};

const CanvasGroupNodeView = ({ node, depth = 0, ruleEngineOperators }: { node: ConditionGroupNode; depth?: number; ruleEngineOperators?: any }) => {
  if (!node || !node.rules) return null;
  const currentOp = node.operator || node.groupLogic || 'AND';

  return (
    <div className={`rounded-lg border p-1.5 space-y-1 ${depth === 0
      ? 'bg-gray-950/80 border-indigo-500/30'
      : depth % 2 === 1
        ? 'bg-gray-900 border-gray-800'
        : 'bg-gray-950 border-indigo-500/20'
      }`}>
      <div className="flex items-center justify-between text-[9px] text-indigo-300 font-bold uppercase tracking-wider border-b border-gray-900 pb-0.5">
        <span>{depth === 0 ? 'Root Group' : `Level ${depth}`}</span>
        <span className={`px-1 py-0.2 text-[8px] font-black rounded ${currentOp === 'OR' ? 'bg-amber-500/20 text-amber-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
          {currentOp}
        </span>
      </div>
      <div className="space-y-1">
        {node.rules.map((child, i) => {
          const opKey = isConditionRow(child) ? (child.op || child.operator || 'equals') : '';
          const isNoValueReq = isConditionRow(child) ? isOperatorWithoutValue(opKey, ruleEngineOperators) : false;
          const displayVal = isConditionRow(child) ? formatConditionDisplayValue(child.value, child.field, opKey, (child as any).type) : '';

          return (
            <React.Fragment key={(child as any).id || i}>
              {i > 0 && (
                <div className="text-[8px] font-black text-gray-200 text-center uppercase my-0.5 tracking-wider">
                  {currentOp}
                </div>
              )}
              {isConditionRow(child) ? (
                <div className="flex items-center justify-between gap-1 text-[10px] bg-gray-900 border border-gray-800 rounded px-2 py-1 text-gray-200">
                  <span className="font-semibold text-gray-200 truncate max-w-[95px]" title={child.field || 'Select Field'}>
                    {child.field || 'Select Field'}
                  </span>
                  <span className="text-[8px] font-mono text-indigo-400 font-bold px-1 py-0.2 bg-indigo-500/10 border border-indigo-500/20 rounded uppercase">
                    {opKey}
                  </span>
                  {child.value !== undefined && child.value !== '' ? (
                    <span className="font-mono text-amber-300 truncate max-w-[105px] font-semibold text-right" title={displayVal}>
                      "{displayVal}"
                    </span>
                  ) : isNoValueReq ? (
                    <span className="text-[9px] text-gray-400 italic truncate max-w-[85px] text-right" title="Not Required">
                      Not Required
                    </span>
                  ) : (
                    <span className="text-[9px] text-gray-400 italic truncate max-w-[85px] text-right" title="Empty value">
                      Empty
                    </span>
                  )}
                </div>
              ) : (
                <CanvasGroupNodeView node={child as ConditionGroupNode} depth={depth + 1} ruleEngineOperators={ruleEngineOperators} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const ConditionsNode = ({ data }: any) => {
  const tree: ConditionGroupNode = data.conditionTree || { operator: 'AND', rules: [] };
  const totalRules = data.conditionsCount ?? 0;
  const totalGroups = data.groupsCount ?? 0;

  return (
    <div className="flex flex-col items-center group cursor-pointer">
      <div
        className={`w-72 rounded-xl bg-gray-900/95 border ${data.selected
          ? 'ring-2 ring-indigo-500 border-indigo-400'
          : 'border-indigo-500/40 hover:border-indigo-500/80'
          } transition-all shadow-2xl p-3 relative duration-150 text-left`}
      >
        <Handle type="target" position={Position.Left} style={{ background: '#6366f1', width: 8, height: 8, left: -4 }} />

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-indigo-400">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M9 12h3a3 3 0 0 0 3-3V8a2 2 0 0 1 2-2h1" />
                <path d="M9 12h3a3 3 0 0 1 3 3v4a2 2 0 0 0 2 2h1" />
              </svg>
            </div>
            <div>
              <div className="text-xs font-bold text-white leading-tight">Conditions Engine</div>
              <div className="text-[9px] text-indigo-300 font-semibold">
                {totalGroups} Group{totalGroups !== 1 ? 's' : ''} ({totalRules} Rule{totalRules !== 1 ? 's' : ''})
              </div>
            </div>
          </div>
          <span className="px-1.5 py-0.5 text-[8px] font-extrabold uppercase rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
            RULES
          </span>
        </div>

        {/* Detailed Condition Tree List */}
        <div className="space-y-2 max-h-80 overflow-y-auto custom-scroll pr-1">
          {(!tree.rules || tree.rules.length === 0) ? (
            <div className="text-[10px] text-gray-200 italic py-2 text-center border border-dashed border-gray-800 rounded-lg">
              No filter conditions set
            </div>
          ) : (
            <CanvasGroupNodeView node={tree} ruleEngineOperators={data.ruleEngineOperators} />
          )}
        </div>

        {/* Handle with hover plus button */}
        <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center">
          <Handle type="source" position={Position.Right} style={{ background: '#6366f1', width: 8, height: 8, right: -4 }} />
          {data.onAddAction && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                data.onAddAction();
              }}
              className="absolute opacity-0 group-hover:opacity-100 w-4 h-4 rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center border border-gray-950 hover:scale-115 active:scale-95 transition-all shadow-md cursor-pointer z-20"
              style={{ right: -5 }}
              title="Add Action"
            >
              +
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ActionNode = ({ data }: any) => (
  <div className="flex flex-col items-center group cursor-pointer">
    <div className={`w-16 h-16 rounded-2xl bg-gray-900 border ${data.is_broken
      ? (data.selected ? 'ring-2 ring-red-500 border-red-500' : 'border-red-500 hover:border-red-400 shadow-red-500/20')
      : (data.selected ? 'ring-2 ring-amber-500 border-transparent' : 'border-gray-800 hover:border-amber-500')
      } transition-all flex items-center justify-center shadow-lg group-hover:scale-105 duration-150 relative`}>
      <Handle type="target" position={Position.Left} style={{ background: data.is_broken ? '#ef4444' : '#06b6d4', width: 6, height: 6 }} />
      {getActionIcon(data.actionType)}
    </div>
    <div className={`mt-1.5 text-[10px] font-bold ${data.is_broken ? 'text-red-400 group-hover:text-red-300' : 'text-gray-200 group-hover:text-white'} transition-colors text-center w-28 truncate select-none leading-none`}>
      {data.label}
    </div>

    {/* Action Stats Badges */}
    <ActionNodeStatsBadges stats={data.stats} />
  </div>
);

const nodeTypes = {
  assetTypeNode: AssetTypeNode,
  triggerNode: TriggerNode,
  conditionsNode: ConditionsNode,
  actionNode: ActionNode,
};

export interface ConditionRuleItem {
  id?: string;
  field: string;
  op?: string;
  operator?: string;
  value?: any;
  type?: string;
  logic?: 'AND' | 'OR';
}

export interface ConditionGroupItem {
  id: string;
  groupLogic?: 'AND' | 'OR';
  rules: ConditionRuleItem[];
}

const TemplateVariableDropdown: React.FC<{
  onSelectVariable: (varName: string) => void;
  ruleEngineActionsCatalog?: any;
  ruleEngineCollections?: any;
  triggerSource?: string;
  disabled?: boolean;
  hasConditions?: boolean;
}> = ({ onSelectVariable, ruleEngineActionsCatalog, ruleEngineCollections, triggerSource, disabled, hasConditions = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as globalThis.Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to extract flat list of strings from template_variables (array or dict object)
  const extractFlatVars = (vars: any): string[] => {
    if (!vars) return [];
    if (Array.isArray(vars)) {
      return vars.filter((v): v is string => typeof v === 'string');
    }
    if (typeof vars === 'object') {
      const flat: string[] = [];
      for (const val of Object.values(vars)) {
        if (Array.isArray(val)) {
          val.forEach((item) => {
            if (typeof item === 'string') flat.push(item);
          });
        } else if (typeof val === 'string') {
          flat.push(val);
        }
      }
      return flat;
    }
    return [];
  };

  // 1. Action Catalog Template Variables
  const catalogActionVars = extractFlatVars(ruleEngineActionsCatalog?.template_variables);
  const actionVariables: string[] = catalogActionVars.length > 0 ? catalogActionVars : [
    "asset.name",
    "asset.ip_address",
    "asset.os_name",
    "asset.asset_type",
    "asset.platform",
    "client.name",
    "agent.name",
    "collection",
    "signal_type",
    "action",
    "severity",
    "message",
    "record_key",
    "timestamp",
    "rule.name"
  ];

  // 2. Collection Template Variables
  const collectionsList: any[] = ruleEngineCollections?.collections || [];

  // Find current active collection for rule trigger
  const activeCollection = collectionsList.find(
    (c: any) => c.key === triggerSource || c.db_table === triggerSource || c.name === triggerSource || c.label === triggerSource
  );

  const activeCollectionVars: string[] = activeCollection ? extractFlatVars(activeCollection.template_variables) : [];

  // Filter helper for search term
  const matchesSearch = (str: string) => {
    if (!searchTerm.trim()) return true;
    return str.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const filteredActionVars = actionVariables.filter(matchesSearch);
  const filteredCollectionVars = activeCollectionVars.filter(matchesSearch);

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setSearchTerm('');
          }
        }}
        className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono text-orange-400 bg-orange-500/10 border border-orange-500/30 rounded-md hover:bg-orange-500/20 hover:border-orange-500/60 transition-all duration-150 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed select-none shadow-sm shadow-orange-500/5"
        title="Insert template variable into text field"
      >
        <span className="font-bold text-orange-300">{`{{ }}`}</span>
        <span className="font-sans font-semibold">+ Variable</span>
        <LucideIcons.ChevronDown className={`w-3 h-3 text-orange-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-64 rounded-xl bg-gray-950 border border-gray-800 shadow-2xl z-50 overflow-hidden flex flex-col max-h-72 border-t-orange-500/40">
          {/* Search bar inside dropdown */}
          <div className="p-2 border-b border-gray-850 bg-gray-900/90">
            <div className="relative flex items-center">
              <LucideIcons.Search className="w-3 h-3 text-gray-400 absolute left-2 pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search variables..."
                className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 pl-7 text-[11px] text-white focus:outline-none focus:border-orange-500 font-sans"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 text-gray-400 hover:text-white"
                >
                  <LucideIcons.X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto custom-scroll p-1 space-y-2">
            {/* Active Collection Variables (if selected and conditions exist) */}
            {hasConditions && activeCollection && (
              <div>
                <div className="px-2 py-1 text-[9px] font-bold text-orange-400 uppercase tracking-wider bg-orange-500/10 rounded flex items-center gap-1.5 mb-0.5">
                  <LucideIcons.Database className="w-3 h-3 text-orange-400 flex-shrink-0" />
                  <span className="truncate">{activeCollection.label || activeCollection.key} Variables</span>
                </div>
                {filteredCollectionVars.length > 0 ? (
                  filteredCollectionVars.map((varName: string) => (
                    <button
                      key={varName}
                      type="button"
                      onClick={() => {
                        onSelectVariable(varName);
                        setIsOpen(false);
                      }}
                      className="w-full text-left px-2.5 py-1 text-[11px] font-mono text-orange-300 hover:text-white hover:bg-orange-500/20 rounded cursor-pointer flex items-center justify-between group transition-colors"
                    >
                      <span className="truncate">{`{{${varName}}}`}</span>
                      <span className="text-[9px] font-sans text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">Insert</span>
                    </button>
                  ))
                ) : (
                  <div className="px-2.5 py-1 text-[10px] text-gray-200 italic">No matching variables</div>
                )}
              </div>
            )}

            {/* Action Catalog Variables */}
            <div>
              <div className="px-2 py-1 text-[9px] font-bold text-amber-400 uppercase tracking-wider bg-amber-500/10 rounded flex items-center gap-1.5 mb-0.5">
                <LucideIcons.Zap className="w-3 h-3 text-amber-400 flex-shrink-0" />
                <span>Action & System Variables</span>
              </div>
              {filteredActionVars.length > 0 ? (
                filteredActionVars.map((varName) => (
                  <button
                    key={varName}
                    type="button"
                    onClick={() => {
                      onSelectVariable(varName);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-2.5 py-1 text-[11px] font-mono text-gray-200 hover:text-white hover:bg-orange-500/20 rounded cursor-pointer flex items-center justify-between group transition-colors"
                  >
                    <span className="truncate">{`{{${varName}}}`}</span>
                    <span className="text-[9px] font-sans text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">Insert</span>
                  </button>
                ))
              ) : (
                <div className="px-2.5 py-1 text-[10px] text-gray-200 italic">No matching action variables</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DynamicActionField: React.FC<{
  field: any;
  actionNode: any;
  handleActionConfigChange: (actionId: string, name: string, value: any) => void;
  isViewMode: boolean;
  ruleEngineActionsCatalog?: any;
  ruleEngineCollections?: any;
  triggerSource?: string;
  hasConditions?: boolean;
}> = ({ field, actionNode, handleActionConfigChange, isViewMode, ruleEngineActionsCatalog, ruleEngineCollections, triggerSource, hasConditions = false }) => {
  const [options, setOptions] = useState<Array<{ label: string; value: string; raw: any }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fieldKey = field.key || field.name;
  const optionLabelKey = field.option_label_key || 'label';
  const optionValueKey = field.option_value_key || 'value';
  const sendAsObject = field.send_as_object === true || field.type === 'object_select';

  // Check dependent parent field value if field.depends_on exists
  const parentRawValue = field.depends_on ? actionNode.config[field.depends_on] : null;
  const parentId = typeof parentRawValue === 'object' && parentRawValue !== null
    ? (parentRawValue[field.option_value_key || 'id'] !== undefined ? parentRawValue[field.option_value_key || 'id'] : parentRawValue.id)
    : parentRawValue;

  useEffect(() => {
    if (!field.api) return;

    // If dependent on another field but parent is not selected yet, clear options
    if (field.depends_on && (parentId === null || parentId === undefined || parentId === '')) {
      setOptions([]);
      return;
    }

    let isMounted = true;
    const fetchOptions = async () => {
      setLoading(true);
      try {
        let url = field.api;
        if (field.depends_on && parentId !== null && parentId !== undefined && field.depends_param) {
          const separator = url.includes('?') ? '&' : '?';
          url = `${url}${separator}${field.depends_param}=${encodeURIComponent(String(parentId))}`;
        }

        const res = await automationApi.fetchDynamicOptions(url);
        if (isMounted) {
          const resData = res.data;
          const rawList = Array.isArray(resData)
            ? resData
            : (Array.isArray(resData?.data) ? resData.data : (Array.isArray(resData?.items) ? resData.items : []));

          const mapped = rawList.map((item: any) => {
            if (typeof item !== 'object' || item === null) {
              return { label: String(item), value: String(item), raw: item };
            }
            const labelVal = item[optionLabelKey] || item.name || item.label || item.title || String(item[optionValueKey] || item.id || item);
            const valueVal = String(item[optionValueKey] !== undefined ? item[optionValueKey] : (item.id !== undefined ? item.id : item));
            return {
              label: String(labelVal),
              value: valueVal,
              raw: item
            };
          });
          setOptions(mapped);
        }
      } catch (err) {
        console.error(`Error fetching dynamic options for field ${fieldKey}:`, err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOptions();
    return () => { isMounted = false; };
  }, [field.api, field.depends_on, parentId, optionLabelKey, optionValueKey, fieldKey]);

  const currentValueRaw = actionNode.config[fieldKey];
  let selectValue = '';
  if (sendAsObject) {
    if (typeof currentValueRaw === 'object' && currentValueRaw !== null) {
      selectValue = String(currentValueRaw[optionValueKey] !== undefined ? currentValueRaw[optionValueKey] : (currentValueRaw.id || ''));
    } else if (currentValueRaw !== undefined && currentValueRaw !== null) {
      selectValue = String(currentValueRaw);
    }
  } else {
    selectValue = currentValueRaw !== undefined && currentValueRaw !== null ? String(currentValueRaw) : '';
  }

  const handleChange = (selectedVal: string) => {
    if (sendAsObject) {
      const match = options.find(o => o.value === selectedVal);
      if (match) {
        handleActionConfigChange(actionNode.id, fieldKey, match.raw);
      } else {
        handleActionConfigChange(actionNode.id, fieldKey, { [optionValueKey]: selectedVal });
      }
    } else {
      handleActionConfigChange(actionNode.id, fieldKey, selectedVal);
    }
  };

  const handleInsertVariable = (varName: string) => {
    const tag = `{{${varName}}}`;
    const currentValue = typeof currentValueRaw === 'string'
      ? currentValueRaw
      : (currentValueRaw !== undefined && currentValueRaw !== null ? String(currentValueRaw) : '');

    let newValue = '';
    if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
      const start = inputRef.current.selectionStart;
      const end = inputRef.current.selectionEnd;
      newValue = currentValue.substring(0, start) + tag + currentValue.substring(end);
    } else {
      newValue = currentValue ? `${currentValue} ${tag}` : tag;
    }

    handleActionConfigChange(actionNode.id, fieldKey, newValue);
  };

  const showInsertVariableDropdown = field.insert_variable === true || (field.insert_variable !== false && field.type === 'template_text');

  const nameLower = (fieldKey || '').toLowerCase();
  const labelLower = (field.label || '').toLowerCase();
  const isLangField =
    field.type === 'language' ||
    nameLower === 'template_language' ||
    nameLower === 'language' ||
    nameLower === 'template_lang' ||
    nameLower === 'lang' ||
    labelLower.includes('template language') ||
    labelLower === 'language';

  if (isLangField) {
    return (
      <SingleSelectLocalSearchDropdown
        value={typeof currentValueRaw === 'string' ? currentValueRaw : (currentValueRaw ? String(currentValueRaw) : 'en')}
        options={WHATSAPP_LANGUAGES}
        onChange={val => handleActionConfigChange(actionNode.id, fieldKey, val || '')}
        disabled={isViewMode}
        placeholder="Select Language"
        buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
      />
    );
  }

  if (field.type === 'template_text' || field.type === 'text') {
    return (
      <div className="space-y-1">
        {showInsertVariableDropdown && (
          <div className="flex items-center justify-between gap-2">
            <label className="text-[10px] text-gray-200 font-bold uppercase">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <TemplateVariableDropdown
              onSelectVariable={handleInsertVariable}
              ruleEngineActionsCatalog={ruleEngineActionsCatalog}
              ruleEngineCollections={ruleEngineCollections}
              triggerSource={triggerSource}
              hasConditions={hasConditions}
              disabled={isViewMode}
            />
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={typeof currentValueRaw === 'string' ? currentValueRaw : (currentValueRaw !== undefined && currentValueRaw !== null ? String(currentValueRaw) : '')}
          onChange={e => handleActionConfigChange(actionNode.id, fieldKey, e.target.value)}
          placeholder={field.placeholder || ''}
          disabled={isViewMode}
          className="w-full bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 font-mono text-[11px] disabled:opacity-75 disabled:cursor-not-allowed"
        />
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 cursor-pointer select-none group">
        <div
          className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${currentValueRaw
            ? 'bg-orange-500 border-orange-500 text-white shadow-sm shadow-orange-500/30'
            : 'bg-gray-950 border-gray-700 group-hover:border-gray-500'
            } ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {Boolean(currentValueRaw) && (
            <svg className="w-3 h-3 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        <input
          type="checkbox"
          checked={Boolean(currentValueRaw)}
          onChange={e => handleActionConfigChange(actionNode.id, fieldKey, e.target.checked)}
          disabled={isViewMode}
          className="sr-only"
        />
        <span className="text-xs text-gray-200 font-semibold">{field.label}</span>
      </label>
    );
  }

  if (field.type === 'dropdown' || field.type === 'object_select' || field.api || field.type === 'select') {
    const staticOptions = field.values ? field.values.map((v: any) => typeof v === 'object' ? { label: v.label, value: String(v.value), raw: v } : { label: String(v), value: String(v), raw: v }) : [];
    const displayOptions = options.length > 0 ? options : staticOptions;

    let placeholderText = "Select...";
    if (loading) {
      placeholderText = "Loading options...";
    } else if (field.depends_on && (parentId === null || parentId === undefined || parentId === '')) {
      placeholderText = `Select ${field.depends_on} first...`;
    }

    return (
      <SingleSelectLocalSearchDropdown
        value={selectValue}
        options={displayOptions}
        onChange={val => handleChange(val !== null && val !== undefined ? String(val) : '')}
        disabled={isViewMode || loading || Boolean(field.depends_on && (parentId === null || parentId === undefined || parentId === ''))}
        className="w-full font-sans"
        placeholder={placeholderText}
        buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
      />
    );
  }

  return (
    <div className="space-y-1">
      {showInsertVariableDropdown && (
        <div className="flex items-center justify-between gap-2">
          <label className="text-[10px] text-gray-200 font-bold uppercase">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <TemplateVariableDropdown
            onSelectVariable={handleInsertVariable}
            ruleEngineActionsCatalog={ruleEngineActionsCatalog}
            ruleEngineCollections={ruleEngineCollections}
            triggerSource={triggerSource}
            hasConditions={hasConditions}
            disabled={isViewMode}
          />
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        value={currentValueRaw !== undefined && currentValueRaw !== null ? (typeof currentValueRaw === 'object' ? JSON.stringify(currentValueRaw) : String(currentValueRaw)) : ''}
        onChange={e => handleActionConfigChange(actionNode.id, fieldKey, e.target.value)}
        placeholder={field.placeholder || ''}
        disabled={isViewMode}
        className="w-full bg-gray-950 border border-gray-800 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 disabled:opacity-75 disabled:cursor-not-allowed"
      />
    </div>
  );
};

export const AutomationEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { ruleName?: string; ruleDescription?: string; assetType?: string } | null;

  const currentLevel = useClientLevel();
  const [ruleClientId, setRuleClientId] = useState<any>(null);

  const [catalog, _setCatalog] = useState<CatalogData | null>(null);
  const [lookups, setLookups] = useState<LookupsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ticketingDirectory, _setTicketingDirectory] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const isViewMode = searchParams.get('mode') === 'view';

  // Rule State
  const [ruleName, setRuleName] = useState(locationState?.ruleName || '');
  const [targetAssetType, setTargetAssetType] = useState(locationState?.assetType || '');
  const [description, setDescription] = useState(locationState?.ruleDescription || '');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('warning');
  const [isActive, setIsActive] = useState(true);
  const [isDefaultRule, setIsDefaultRule] = useState(false);
  const [isGlobalRule, setIsGlobalRule] = useState(false);
  const isClientLevel = currentLevel && currentLevel.name !== 'global';
  const isTriggerConditionDisabled = isViewMode || isDefaultRule || (isGlobalRule && isClientLevel);

  // Default: don't pre-populate any node for new rules
  const [triggerSource, setTriggerSource] = useState(id ? 'AssetPatch' : '');
  const [triggerSignals, setTriggerSignals] = useState<string[]>([]);
  const [triggerOptions, setTriggerOptions] = useState<Record<string, any>>({
    target_asset_type: locationState?.assetType || ''
  });

  // Recursive Condition Tree Engine
  const [conditionTree, setConditionTree] = useState<ConditionGroupNode>({
    operator: 'AND',
    groupLogic: 'AND',
    rules: []
  });

  const onChangeTree = useCallback((path: number[], fn: (n: any) => any) => {
    setConditionTree(prev => updateAt(prev, path, fn));
  }, []);

  const conditionGroups = useMemo(() => {
    if (!conditionTree.rules) return [];
    return conditionTree.rules.map((child, idx) => {
      if (isConditionRow(child)) {
        return {
          id: child.id || `g-${idx}`,
          groupLogic: 'AND' as const,
          rules: [child]
        };
      }
      return {
        id: child.id || `g-${idx}`,
        groupLogic: (child as any).operator || (child as any).groupLogic || 'AND' as const,
        rules: (child.rules || []).map((r: any) => isConditionRow(r) ? r : { field: '', op: 'equals', value: '' })
      };
    });
  }, [conditionTree]);

  const conditions = useMemo(() => {
    const flatten = (node: any): any[] => {
      if (!node) return [];
      if (isConditionRow(node)) return [node];
      return (node.rules || []).flatMap(flatten);
    };
    return flatten(conditionTree);
  }, [conditionTree]);


  const [actions, setActions] = useState<Array<{ id: string; type: string; config: Record<string, any>; requires_approval?: boolean; condition_id?: string; integration_id?: any; is_active?: boolean; is_broken?: boolean; actionId?: any; db_id?: any; client_id?: any; is_edited?: boolean }>>(
    !id ? [{ id: 'a1', type: 'alert', config: { title: 'Default System Alert', severity: 'medium', message: 'Rule trigger condition matched' }, is_edited: true }] : []
  );

  // Rule engine collections, operators & actions catalog from API
  const [ruleEngineCollections, setRuleEngineCollections] = useState<any>(null);
  const [ruleEngineOperators, setRuleEngineOperators] = useState<any>(null);
  const [ruleEngineActionsCatalog, setRuleEngineActionsCatalog] = useState<any>(null);
  const [assetTypeOptions, setAssetTypeOptions] = useState<SelectOption[]>(ALL_ASSET_TYPE_OPTIONS);

  // Rule Statistics from API
  const [ruleStats, setRuleStats] = useState<any | null>(null);

  // Advanced & Metadata
  const [cooldown, setCooldown] = useState(15);
  const [autoResolve, setAutoResolve] = useState(true);

  const [showNameModal, setShowNameModal] = useState(false);
  const [tempRuleName, setTempRuleName] = useState(locationState?.ruleName || '');
  const [tempRuleDescription, setTempRuleDescription] = useState(locationState?.ruleDescription || '');
  const [tempAssetType, setTempAssetType] = useState(locationState?.assetType || '');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // React Flow Elements state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowPositions, setFlowPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  useEffect(() => {
    if (reactFlowInstance) {
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.25, duration: 300 });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isSidebarOpen, actions.length, nodes.length, reactFlowInstance]);
  const [showActionSelector, setShowActionSelector] = useState(false);

  // Dry run testing state
  const [testingAssetId, setTestingAssetId] = useState<string>('42');
  const [testingPayload, setTestingPayload] = useState<string>('{\n  "port_name": "GigabitEthernet0/1",\n  "status": "down"\n}');
  const [testResult, setTestResult] = useState<any>(null);
  const [testRunning, setTestRunning] = useState(false);

  // Sidebar / Drawers tab
  const [rightPanelTab, setRightPanelTab] = useState<'config' | 'advanced' | 'test'>('config');

  // Resizable sidebar state
  const DEFAULT_SIDEBAR_WIDTH = 420;
  const [sidebarWidth, setSidebarWidth] = useState<number>(DEFAULT_SIDEBAR_WIDTH);
  const [isResizingSidebar, setIsResizingSidebar] = useState<boolean>(false);

  // Reset sidebar width to default whenever sidebar is closed
  useEffect(() => {
    if (!isSidebarOpen) {
      setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleCloseConfigSidebar = () => {
      setIsSidebarOpen(false);
    };
    window.addEventListener('closeAutomationConfigSidebar', handleCloseConfigSidebar);
    return () => window.removeEventListener('closeAutomationConfigSidebar', handleCloseConfigSidebar);
  }, []);

  // When user changes the client from the header/selector while inside edit rule,
  // immediately exit out to the rules table for that newly selected client.
  const initialClientKeyRef = useRef<string>(
    currentLevel ? `${currentLevel.name}_${currentLevel.id || currentLevel.client_id || ''}` : 'global'
  );

  useEffect(() => {
    const handleLevelChange = () => {
      navigate('/automation?tab=rules');
    };
    window.addEventListener('client-level-changed', handleLevelChange);
    return () => window.removeEventListener('client-level-changed', handleLevelChange);
  }, [navigate]);

  useEffect(() => {
    const currentKey = currentLevel ? `${currentLevel.name}_${currentLevel.id || currentLevel.client_id || ''}` : 'global';
    if (initialClientKeyRef.current !== currentKey) {
      navigate('/automation?tab=rules');
    }
  }, [currentLevel, navigate]);

  // Resizing mouse drag handlers
  const handleMouseDownResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingSidebar(true);
  };

  useEffect(() => {
    if (!isResizingSidebar) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 320 && newWidth <= 800) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingSidebar]);

  const fetchCatalogAndLookups = async (assetTypeToUse?: string) => {
    try {
      const typeForCollections = assetTypeToUse || targetAssetType || 'all';

      const activeClientId = (ruleClientId !== null && ruleClientId !== undefined)
        ? ruleClientId
        : (currentLevel && currentLevel.name !== 'global' && (currentLevel.id || currentLevel.client_id))
          ? (isNaN(Number(currentLevel.id || currentLevel.client_id)) ? (currentLevel.id || currentLevel.client_id) : Number(currentLevel.id || currentLevel.client_id))
          : undefined;

      const tagParams = withClientIdParams({
        limit: 1000,
        ...(activeClientId !== undefined ? { client_id: activeClientId } : {})
      });

      const [collectionsRes, operatorsRes, actionsCatalogRes, clientsRes, tagsRes, catalogAssetsRes] = await Promise.allSettled([
        automationApi.getRuleEngineCollections(typeForCollections),
        automationApi.getRuleEngineOperators(),
        automationApi.getRuleEngineActionsCatalog(),
        baseapi.get('/clients', { params: { limit: 1000 } }),
        baseapi.get('/tags', { params: tagParams }),
        automationApi.getRuleEngineCatalogAssets()
      ]);

      if (collectionsRes.status === 'fulfilled' && collectionsRes.value?.data) {
        const cData = collectionsRes.value.data.data || collectionsRes.value.data;
        if (cData && cData.collections) {
          setRuleEngineCollections(cData);
        }
      }

      if (operatorsRes.status === 'fulfilled' && operatorsRes.value?.data) {
        const opData = operatorsRes.value.data.data || operatorsRes.value.data;
        if (opData) {
          setRuleEngineOperators(opData);
        } else {
          toast.error('Failed to load operators catalog from API');
        }
      } else {
        toast.error('Failed to load operators catalog from API');
      }

      if (actionsCatalogRes.status === 'fulfilled' && actionsCatalogRes.value?.data) {
        const actData = actionsCatalogRes.value.data.data || actionsCatalogRes.value.data;
        setRuleEngineActionsCatalog(actData);
      }

      if (catalogAssetsRes.status === 'fulfilled' && catalogAssetsRes.value?.data) {
        const response = catalogAssetsRes.value;
        const rawData = Array.isArray(response.data?.data)
          ? response.data.data
          : (Array.isArray(response.data) ? response.data : []);

        if (Array.isArray(rawData) && rawData.length > 0) {
          const mappedOptions: SelectOption[] = rawData.map((item: any) => ({
            value: item.id || item.value,
            label: item.label || item.name,
            icon: getLucideIcon(item.icon)
          }));
          setAssetTypeOptions(mappedOptions);
        }
      }

      let fetchedClients: any[] = [];
      if (clientsRes.status === 'fulfilled' && clientsRes.value?.data) {
        const val = clientsRes.value.data;
        fetchedClients = val.data?.clients || val.clients || val.data || (Array.isArray(val) ? val : []);
      }

      let fetchedTags: any[] = [];
      if (tagsRes.status === 'fulfilled' && tagsRes.value?.data) {
        const val = tagsRes.value.data;
        fetchedTags = val.data?.tags || val.tags || val.data || (Array.isArray(val) ? val : []);
      }

      setLookups((prev: LookupsData | null) => ({
        clients: fetchedClients,
        tags: fetchedTags,
        integrations: prev?.integrations || [],
        device_types: prev?.device_types || [],
        scripts: prev?.scripts || [],
        oncall_schedules: prev?.oncall_schedules || [],
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadRule = async (ruleId: string) => {
    if (ruleId === '999') return; // Demo template id fallback
    try {
      const res = await automationApi.getRule(ruleId);
      if (res.data?.status || res.data?.rule) {
        const rule = res.data.rule || res.data.data || res.data;

        setRuleName(rule.name || '');
        setDescription(rule.description || '');
        setSeverity(rule.severity || 'medium');
        setIsActive(rule.is_active !== false);
        if (rule.client_id !== undefined && rule.client_id !== null) {
          setRuleClientId(rule.client_id);
        }

        if (rule.stats) {
          setRuleStats(rule.stats);
        }

        // Trigger Configuration
        const trig = rule.rule_config?.trigger || rule.trigger || {};
        const collectionKey = trig.collection || trig.source || 'Applications';
        setTriggerSource(collectionKey);
        setTriggerSignals(trig.signal_types || []);

        const targetType = trig.asset_types?.[0] || trig.target_asset_type || 'local_asset';
        setTargetAssetType(targetType);
        await fetchCatalogAndLookups(targetType);

        // Scope Configuration
        const scopeData = rule.rule_config?.scope || rule.scope || {};
        const loadedClientIds = scopeData.client_ids || scopeData.include_clients || trig.client_ids || trig.include_clients || [];
        const loadedTagIds = scopeData.tag_ids || scopeData.include_tags || trig.tag_ids || trig.include_tags || [];
        const loadedAssetIds = scopeData.asset_ids || trig.asset_ids || [];
        const loadedExcludeTags = scopeData.exclude_tags || trig.exclude_tags || [];

        const resolvedIncludeClients = loadedClientIds.map((val: any) => {
          const match = lookups?.clients?.find((c: any) => String(c.id) === String(val) || String(c.name) === String(val));
          return match ? match.name : val;
        });

        const resolvedIncludeTags = loadedTagIds.map((val: any) => {
          const match = lookups?.tags?.find((t: any) => String(t.id) === String(val) || String(t.name) === String(val));
          return match ? match.name : val;
        });

        setTriggerOptions({
          target_asset_type: targetType,
          ...trig,
          ...scopeData,
          include_clients: resolvedIncludeClients,
          include_tags: resolvedIncludeTags,
          client_ids: loadedClientIds,
          tag_ids: loadedTagIds,
          asset_ids: loadedAssetIds,
          exclude_tags: loadedExcludeTags,
        });

        // Conditions Configuration
        const rawConditions = rule.rule_config?.conditions || rule.conditions;
        if (rawConditions) {
          const parseConditionTree = (node: any): any => {
            if (!node) return null;
            if (node.field) {
              return {
                id: `r-${Math.random().toString(36).substr(2, 5)}`,
                field: node.field,
                op: node.operator || node.op || 'equals',
                operator: node.operator || node.op || 'equals',
                value: node.value ?? '',
                type: node.type
              };
            }
            if (node.rules && Array.isArray(node.rules)) {
              return {
                id: `g-${Math.random().toString(36).substr(2, 5)}`,
                operator: node.operator || node.groupLogic || 'AND',
                groupLogic: node.operator || node.groupLogic || 'AND',
                rules: node.rules.map(parseConditionTree).filter(Boolean)
              };
            }
            return null;
          };

          const parsedTree = parseConditionTree(rawConditions);
          if (parsedTree) {
            setConditionTree(parsedTree.field ? { operator: 'AND', rules: [parsedTree] } : parsedTree);
          }
        }

        // Actions Configuration
        const ruleActionsList = (Array.isArray(rule.all_actions) && rule.all_actions.length > 0)
          ? rule.all_actions
          : (Array.isArray(rule.actions) ? rule.actions : []);
        if (ruleActionsList.length > 0) {
          const loadedActions = ruleActionsList.map((act: any) => {
            const rawType = act.action_type || act.type || act.integration_type || 'ticket';
            const normType = rawType === 'microsoft_teams' ? 'teams' : rawType;
            return {
              id: normType,
              actionId: act.id,
              db_id: act.id,
              type: normType,
              integration_id: act.integration_id,
              is_active: act.is_active !== false,
              is_broken: act.is_broken === true,
              config: act.config || { ...act },
              client_id: act.client_id !== undefined ? act.client_id : null,
              is_edited: false
            };
          });
          setActions(loadedActions);
        }

        // Positions Configuration
        const posObj = rule.positions || rule.flow?.positions;
        if (posObj) {
          const flatPositions: Record<string, { x: number; y: number }> = {};
          if (posObj.asset_type) flatPositions['asset_type'] = posObj.asset_type;
          if (posObj.asset) flatPositions['asset_type'] = posObj.asset;
          if (posObj.trigger) flatPositions['trigger'] = posObj.trigger;
          if (posObj.conditions) flatPositions['conditions'] = posObj.conditions;
          if (posObj.actions && typeof posObj.actions === 'object') {
            Object.entries(posObj.actions).forEach(([actType, actPos]: [string, any]) => {
              const normType = actType === 'microsoft_teams' ? 'teams' : actType;
              flatPositions[normType] = actPos;
              flatPositions[actType] = actPos;
            });
          }
          setFlowPositions({ ...posObj, ...flatPositions });
        }

        // Advanced Settings
        setCooldown(rule.cooldown_minutes ?? 15);
        setAutoResolve(rule.auto_resolve ?? false);
        setIsDefaultRule(rule.is_default === true);
        setIsGlobalRule(rule.is_global === true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Error fetching rule details');
    }
  };

  const handleAddGroup = useCallback(() => {
    const collectionsList = ruleEngineCollections?.collections || [];
    const currCol = collectionsList.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
    const defaultField = getDefaultFieldPath(currCol?.fields || []);
    const parsed = parseFieldPath(defaultField, currCol?.fields || []);
    const fieldObj = parsed.subField || parsed.topField;
    const defaultType = fieldObj?.type || 'string';
    const initialRule: ConditionRuleItem = {
      id: `r-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      field: defaultField,
      op: 'equals',
      operator: 'equals',
      value: '',
      type: defaultType
    };
    const newGrp: ConditionGroupNode = {
      id: `g-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      operator: 'AND',
      groupLogic: 'AND',
      rules: [initialRule]
    };
    setConditionTree(prev => ({
      ...prev,
      rules: [...(prev.rules || []), newGrp]
    }));
  }, [triggerSource, ruleEngineCollections]);

  const handleAddCondition = useCallback(() => {
    if (!triggerSource) {
      toast.error('Please select a trigger event source first');
      setSelectedNodeId(null);
      return;
    }
    if (!conditionTree.rules || conditionTree.rules.length === 0) {
      handleAddGroup();
    }
    setSelectedNodeId('conditions');
    setRightPanelTab('config');
    setIsSidebarOpen(true);
  }, [triggerSource, conditionTree, handleAddGroup]);

  const handleTriggerAddActionForCondition = useCallback((conditionNodeId: string) => {
    setSelectedNodeId(conditionNodeId);
    setShowActionSelector(true);
  }, []);

  // Update React Flow nodes & edges whenever relevant state changes
  useEffect(() => {
    const currentCollection = ruleEngineCollections?.collections?.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
    const catalogTriggers = (catalog as any)?.trigger_events || (catalog as any)?.sources || [];

    const currentTriggerDetails = catalogTriggers.find((t: any) => t.type === triggerSource || t.name === triggerSource);

    // Dynamic node position updates
    setNodes((prevNodes) => {
      const prevPositions = prevNodes.reduce((acc, node) => {
        acc[node.id] = node.position;
        return acc;
      }, {} as Record<string, { x: number; y: number }>);

      const positions = { ...prevPositions, ...flowPositions };

      const newNodes: Node[] = [];

      // 1. Asset Type Node (Always Rendered!)
      const assetTypeOption = assetTypeOptions.find(o => o.value === targetAssetType);
      const assetTypeLabel = assetTypeOption?.label || targetAssetType || 'Target Asset';

      newNodes.push({
        id: 'asset_type',
        type: 'assetTypeNode',
        data: {
          label: assetTypeLabel,
          targetAssetType,
          selected: selectedNodeId === 'asset_type' || (selectedNodeId === 'trigger' && !triggerSource),
        },
        position: flowPositions['asset_type'] || positions['asset_type'] || { x: 50, y: 110 },
      });

      // 2. Collection / Trigger Node (Always Rendered!)
      const triggerLabel = triggerSource
        ? (currentCollection?.label || currentTriggerDetails?.label || triggerSource.replace('_', ' '))
        : 'Collection Source';
      const collectionIcon = currentCollection?.icon || currentTriggerDetails?.icon;

      newNodes.push({
        id: 'trigger',
        type: 'triggerNode',
        data: {
          label: triggerLabel,
          triggerSource,
          icon: collectionIcon,
          targetAssetType,
          selected: selectedNodeId === 'trigger',
          onAddCondition: (triggerSource && !isTriggerConditionDisabled) ? handleAddCondition : undefined,
          hasCondition: (conditionTree.rules && conditionTree.rules.length > 0),
          stats: ruleStats
        },
        position: flowPositions['trigger'] || positions['trigger'] || { x: 230, y: 110 },
      });

      // 3. Single Conditions Node (Only rendered when triggerSource is selected)
      if (triggerSource) {
        const countGroupsAndRules = (node: ConditionGroupNode): { groups: number; rules: number } => {
          if (!node || !node.rules) return { groups: 0, rules: 0 };
          let grpCount = 1;
          let ruleCount = 0;
          for (const child of node.rules) {
            if (isConditionRow(child)) {
              ruleCount += 1;
            } else {
              const res = countGroupsAndRules(child as ConditionGroupNode);
              grpCount += res.groups;
              ruleCount += res.rules;
            }
          }
          return { groups: grpCount, rules: ruleCount };
        };

        const treeCounts = countGroupsAndRules(conditionTree);
        const hasAnyRules = (conditionTree.rules && conditionTree.rules.length > 0);

        newNodes.push({
          id: 'conditions',
          type: 'conditionsNode',
          data: {
            label: `${hasAnyRules ? treeCounts.groups : 0} Group(s) (${treeCounts.rules} rule(s))`,
            conditionTree: conditionTree,
            groupsCount: hasAnyRules ? treeCounts.groups : 0,
            conditionsCount: treeCounts.rules,
            conditionGroups: conditionGroups,
            ruleEngineOperators: ruleEngineOperators,
            selected: selectedNodeId === 'conditions' || selectedNodeId?.startsWith('condition-'),
            onAddAction: !isViewMode ? () => handleTriggerAddActionForCondition('conditions') : undefined
          },
          position: flowPositions['conditions'] || positions['conditions'] || { x: 410, y: 110 },
        });
      }

      // 4. Actions Nodes
      actions.forEach((act, idx) => {
        const actionsList = ruleEngineActionsCatalog?.actions || (Array.isArray(ruleEngineActionsCatalog) ? ruleEngineActionsCatalog : []);
        const actionLabel = catalog?.action_types?.find(a => a.type === act.type)?.label ||
          actionsList.find((a: any) => a.key === act.type || a.type === act.type)?.label ||
          act.type;

        const actionPos = flowPositions[act.id] || flowPositions[act.type] || flowPositions[act.type === 'teams' ? 'microsoft_teams' : act.type] || positions[act.id];
        const actKey = act.type === 'teams' ? 'microsoft_teams' : act.type;
        const actStats = ruleStats?.by_action?.[act.type] || ruleStats?.by_action?.[actKey];

        newNodes.push({
          id: act.id,
          type: 'actionNode',
          data: {
            label: `${actionLabel} (${act.id})`,
            actionType: act.type,
            selected: selectedNodeId === act.id,
            is_broken: act.is_broken === true,
            stats: actStats
          },
          position: actionPos || { x: 590, y: 110 + idx * 90 },
        });
      });

      return newNodes;
    });

    const newEdges: Edge[] = [];
    // Edge 1: Asset Type -> Collection / Trigger
    newEdges.push({
      id: 'e-asset-trig',
      source: 'asset_type',
      target: 'trigger',
      style: { stroke: '#3b82f6', strokeWidth: 2 },
    });

    // Edge 2: Collection / Trigger -> Conditions
    if (triggerSource) {
      newEdges.push({
        id: 'e-trig-cond',
        source: 'trigger',
        target: 'conditions',
        style: { stroke: '#f97316', strokeWidth: 2 },
      });

      // Edge 3: Conditions -> Actions
      actions.forEach((act) => {
        const isBroken = act.is_broken === true;
        newEdges.push({
          id: `e-cond-${act.id}`,
          source: 'conditions',
          target: act.id,
          style: { stroke: isBroken ? '#ef4444' : '#615fff', strokeWidth: 2 },
        });
      });
    }

    setEdges(newEdges);
  }, [triggerSource, conditionGroups, actions, selectedNodeId, catalog, ruleEngineCollections, ruleEngineActionsCatalog, flowPositions, setNodes, setEdges, handleAddCondition, handleTriggerAddActionForCondition, isViewMode, conditionTree, ruleStats]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const initialAssetType = locationState?.assetType || 'all';
      if (id) {
        setShowNameModal(false);
        await fetchCatalogAndLookups(initialAssetType);
        await loadRule(id);
      } else if (locationState?.ruleName) {
        setRuleName(locationState.ruleName);
        if (locationState.ruleDescription !== undefined) {
          setDescription(locationState.ruleDescription);
        }
        setTargetAssetType(initialAssetType);
        setTriggerOptions(prev => ({ ...prev, target_asset_type: initialAssetType }));
        setShowNameModal(false);
        await fetchCatalogAndLookups(initialAssetType);
        setSelectedNodeId('trigger');
        setIsSidebarOpen(true);
        setRightPanelTab('config');
      } else {
        setShowNameModal(true);
        await fetchCatalogAndLookups(initialAssetType);
      }
      setLoading(false);
    };
    init();
  }, [id, locationState]);

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tempRuleName.trim() && tempAssetType) {
      const selectedType = tempAssetType || 'all';
      setRuleName(tempRuleName.trim());
      setDescription(tempRuleDescription.trim());
      setTargetAssetType(selectedType);
      setTriggerOptions(prev => ({ ...prev, target_asset_type: selectedType }));
      fetchCatalogAndLookups(selectedType);
      setShowNameModal(false);
      setSelectedNodeId('trigger');
      setIsSidebarOpen(true);
      setRightPanelTab('config');
    } else {
      toast.error('Rule name and target asset type are required');
    }
  };

  const handleCancelNameModal = () => {
    setShowNameModal(false);
    if (!id && !ruleName) {
      navigate('/automation?tab=rules');
    }
  };



  const onNodeClick = (_: any, node: Node) => {
    setSelectedNodeId(node.id);
    setRightPanelTab('config');
    setIsSidebarOpen(true);
  };



  const isSameActionType = (typeA?: string, typeB?: string): boolean => {
    if (!typeA || !typeB) return false;
    const a = String(typeA).toLowerCase().trim();
    const b = String(typeB).toLowerCase().trim();
    if (a === b) return true;
    if ((a === 'teams' && b === 'microsoft_teams') || (a === 'microsoft_teams' && b === 'teams')) return true;
    if ((a === 'ticket' && b === 'ticketing') || (a === 'ticketing' && b === 'ticket')) return true;
    return false;
  };

  const handleAddAction = (actionType: string) => {
    const isAlreadyAdded = actions.some(a => isSameActionType(a.type, actionType));
    if (isAlreadyAdded) {
      toast.error(`The "${actionType}" action type is already added to this workflow.`);
      return;
    }

    const newId = `a${actions.length + 1}`;
    let config: any = {};
    if (actionType === 'alert') {
      config = { title: 'Rule Triggered Alert', severity: 'medium', message: 'Alert notification for {{record_key}}' };
    } else if (actionType === 'email') {
      config = { recipients: 'admin@company.com' };
    } else if (actionType === 'slack') {
      config = { channel: '#alerts' };
    } else if (actionType === 'whatsapp') {
      config = { phone_number: '+1234567890', template_language: 'en' };
    } else if (actionType === 'telegram') {
      config = { chat_id: '123456' };
    } else if (actionType === 'teams') {
      config = { webhook_url: 'https://outlook.office.com/...' };
    } else if (actionType === 'sms') {
      config = { to: '+1234567890' };
    } else if (actionType === 'webhook') {
      config = { url: 'https://api.company.com/webhook' };
    }

    // Determine associated condition
    let condition_id = undefined;
    if (selectedNodeId && selectedNodeId.startsWith('condition-')) {
      condition_id = selectedNodeId;
    } else if (selectedNodeId === 'conditions' && conditions.length > 0) {
      condition_id = 'condition-0';
    } else if (currentActionNode && currentActionNode.condition_id) {
      condition_id = currentActionNode.condition_id;
    } else if (conditions.length > 0) {
      condition_id = 'condition-0';
    }

    setActions(prev => [...prev, { id: newId, type: actionType, config, condition_id, is_edited: true }]);
    setSelectedNodeId(newId);
    setShowActionSelector(false);
  };

  const isAlertAction = (act: any): boolean => {
    if (!act) return false;
    const typeStr = String(act.type || act.key || act.integration_type || act.id || '').toLowerCase();
    return typeStr === 'alert' || typeStr === 'create_alert' || typeStr === 'create_event_alert';
  };

  const handleRemoveAction = (actionId: string) => {
    const targetAction = actions.find(act => act.id === actionId);
    if (isAlertAction(targetAction)) {
      toast.error('Alert action cannot be deleted.');
      return;
    }
    if (actions.length === 1) {
      toast.error('At least one action is required for a workflow.');
      return;
    }
    setActions(prev => prev.filter(act => act.id !== actionId));
    if (selectedNodeId === actionId) setSelectedNodeId(null);
  };

  const handleActionChange = (actionId: string, key: string, value: any) => {
    if (key === 'type') {
      const isAlreadyAdded = actions.some(a => a.id !== actionId && isSameActionType(a.type, value));
      if (isAlreadyAdded) {
        toast.error(`The "${value}" action type is already being used by another action.`);
        return;
      }
    }
    setActions(prev => prev.map(act => act.id === actionId ? { ...act, [key]: value, is_edited: true } : act));
  };

  const handleActionConfigChange = (actionId: string, name: string, value: any) => {
    setActions(prev => prev.map(act => {
      if (act.id === actionId) {
        return {
          ...act,
          config: {
            ...act.config,
            [name]: value
          },
          is_edited: true
        };
      }
      return act;
    }));
  };

  const handleAddScopeItem = (field: string, item: any) => {
    setTriggerOptions(prev => {
      const currentList = prev[field] || [];
      if (currentList.includes(item)) return prev;
      const newList = [...currentList, item];
      const updated: Record<string, any> = {
        ...prev,
        [field]: newList
      };
      if (field === 'include_clients') {
        updated.client_ids = newList;
      } else if (field === 'include_tags') {
        updated.tag_ids = newList;
      }
      return updated;
    });
  };

  const handleRemoveScopeItem = (field: string, item: any) => {
    setTriggerOptions(prev => {
      const currentList = prev[field] || [];
      const newList = currentList.filter((x: any) => x !== item);
      const updated: Record<string, any> = {
        ...prev,
        [field]: newList
      };
      if (field === 'include_clients') {
        updated.client_ids = newList;
      } else if (field === 'include_tags') {
        updated.tag_ids = newList;
      }
      return updated;
    });
  };

  const formatConditionsForPayload = (node: any): any => {
    if (!node) return null;
    if (isConditionRow(node)) {
      const op = node.op || node.operator || 'equals';
      const collectionsList = ruleEngineCollections?.collections || [];
      const currCol = collectionsList.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
      const colFields = currCol?.fields || [];
      const parsed = parseFieldPath(node.field, colFields);
      const selectedFieldObj = parsed.subField || parsed.topField;
      const fieldType = node.type || selectedFieldObj?.type || 'string';

      const ruleObj: any = {
        field: node.field,
        operator: op,
        type: fieldType
      };
      if (node.value !== undefined && node.value !== '') {
        ruleObj.value = node.value;
      }
      return ruleObj;
    }

    if (node.rules && Array.isArray(node.rules)) {
      const cleanRules = node.rules.map(formatConditionsForPayload).filter(Boolean);
      if (cleanRules.length === 0) return null;
      return {
        operator: node.operator || node.groupLogic || 'AND',
        rules: cleanRules
      };
    }

    return null;
  };

  const handleSave = async () => {
    if (conditionTree.rules && conditionTree.rules.length > 0) {
      const validateConditionTree = (node: any): string | null => {
        if (!node) return null;
        if (isConditionRow(node)) {
          if (!node.field) return 'Please select a field path for all condition rules';
          const collectionsList = ruleEngineCollections?.collections || [];
          const currCol = collectionsList.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
          const colFields = currCol?.fields || [];
          const parsed = parseFieldPath(node.field, colFields);
          const selectedFieldObj = parsed.subField || parsed.topField;
          const fieldType = selectedFieldObj?.type || 'string';
          const opsForType = getOperatorsForType(ruleEngineOperators, ruleEngineCollections, fieldType);
          const currentOp = node.op || node.operator || (opsForType[0]?.key || opsForType[0]?.value || '');
          const currentOpDef = opsForType.find((o: any) => (o.key || o.value) === currentOp) || opsForType[0];
          const requiresValue = currentOpDef ? currentOpDef.requires_value !== false : true;

          if (requiresValue && (node.value === undefined || node.value === null || String(node.value).trim() === '')) {
            return `Value is compulsory for condition field "${selectedFieldObj?.label || node.field}" with operator "${currentOpDef?.label || currentOp}"`;
          }
          return null;
        }
        if (node.rules && Array.isArray(node.rules)) {
          for (const child of node.rules) {
            const err = validateConditionTree(child);
            if (err) return err;
          }
        }
        return null;
      };

      const errorMsg = validateConditionTree(conditionTree);
      if (errorMsg) {
        toast.error(errorMsg);
        return;
      }
    }

    const formattedConditions = (conditionTree.rules && conditionTree.rules.length > 0)
      ? formatConditionsForPayload(conditionTree)
      : null;

    const rawClients = triggerOptions.include_clients || triggerOptions.client_ids || [];
    const clientIds = rawClients.map((val: any) => {
      const match = lookups?.clients?.find((c: any) => String(c.name) === String(val) || String(c.id) === String(val));
      return match ? match.id : val;
    });

    const rawTags = triggerOptions.include_tags || triggerOptions.tag_ids || [];
    const tagIds = rawTags.map((val: any) => {
      const match = lookups?.tags?.find((t: any) => String(t.name) === String(val) || String(t.id) === String(val));
      return match ? match.id : val;
    });

    const scopeObj = {
      client_ids: clientIds,
      tag_ids: tagIds,
      asset_ids: triggerOptions.asset_ids || [],
      include_clients: triggerOptions.include_clients || [],
      include_tags: triggerOptions.include_tags || [],
      exclude_tags: triggerOptions.exclude_tags || []
    };

    const rule_config = {
      trigger: {
        collection: triggerSource || 'Applications',
        signal_types: triggerSignals.length > 0 ? triggerSignals : ['updated'],
        asset_types: [targetAssetType],
        first_scan_check: false
      },
      scope: scopeObj,
      conditions: formattedConditions
    };

    // const trigger_config = {
    //   name: `${ruleName || 'Automation'} Trigger`,
    //   event_type: `${(ruleName || 'automation').toLowerCase().replace(/[^a-z0-9]+/g, '_')}_alert`,
    //   is_global: true,
    //   client_id: null
    // };

    const notification_configs: any[] = [];

    actions.forEach((act) => {
      const cfg = act.config || {};
      if (act.type !== 'ticket' && act.type !== 'rundesk') {
        const integration_type = act.type === 'teams' ? 'microsoft_teams' : act.type;
        notification_configs.push({
          integration_type: integration_type,
          channel_name: cfg.channel_name || cfg.channel || 'TEST INTEGRATION CHANNEL',
          channel_id: cfg.channel_id || cfg.webhook_url || cfg.chat_id || 'default_channel_id',
          channel_open_message: cfg.channel_open_message || cfg.subject || cfg.message || `📝 ALERT: ${ruleName} for {{record_key}} on {{asset.name}}`,
          channel_close_message: cfg.channel_close_message || '',
          is_active: cfg.is_active !== false
        });
      }
    });

    const activeClientId = (currentLevel && currentLevel.name !== 'global' && currentLevel.id)
      ? (isNaN(Number(currentLevel.id)) ? currentLevel.id : Number(currentLevel.id))
      : null;

    const finalClientId = ruleClientId !== null ? ruleClientId : activeClientId;

    const payload: any = {
      client_id: finalClientId,
      name: ruleName,
      description: description || '',
      severity: severity || 'medium',
      cooldown_minutes: cooldown || 15,
      auto_resolve: autoResolve,
      first_scan_check: false,
      rule_config
    };



    if (notification_configs.length > 0) {
      payload.notification_configs = notification_configs;
    }

    const formattedActions = actions.map((act: any) => {
      const actType = act.type === 'teams' ? 'microsoft_teams' : act.type;
      const actionClientId = activeClientId === null
        ? null
        : (act.is_edited ? activeClientId : (act.client_id ?? null));

      const actionItem: any = {
        action_type: actType,
        type: actType,
        integration_id: act.integration_id !== undefined ? act.integration_id : undefined,
        is_active: act.is_active !== false,
        config: act.config || {},
        client_id: actionClientId
      };
      if (act.actionId || act.db_id) {
        actionItem.id = act.actionId || act.db_id;
      }
      return actionItem;
    });

    const assetTypeNodeObj = nodes.find(n => n.id === 'asset_type');
    const triggerNodeObj = nodes.find(n => n.id === 'trigger');
    const conditionsNodeObj = nodes.find(n => n.id === 'conditions');

    const actionPositionsMap: Record<string, { x: number; y: number }> = {};
    actions.forEach((act, idx) => {
      const actNode = nodes.find(n => n.id === act.id);
      const pos = actNode?.position || flowPositions[act.id] || { x: 550, y: 110 + idx * 90 };
      const keyName = act.type === 'teams' ? 'microsoft_teams' : (act.type || act.id);
      actionPositionsMap[keyName] = pos;
    });

    const positionsObj = {
      asset_type: assetTypeNodeObj?.position || flowPositions['asset_type'] || flowPositions['asset'] || { x: 50, y: 110 },
      trigger: triggerNodeObj?.position || flowPositions['trigger'] || { x: 230, y: 110 },
      conditions: conditionsNodeObj?.position || flowPositions['conditions'] || { x: 410, y: 110 },
      actions: actionPositionsMap
    };

    if (id && id !== '999') {
      const numId = Number(id);
      payload.id = isNaN(numId) ? id : numId;
    }

    payload.positions = positionsObj;
    payload.actions = formattedActions;

    const loader = toast.loading('Saving automation rule...');
    try {
      if (id && id !== '999') {
        let params = {};
        if (isDefaultRule || (isGlobalRule && isClientLevel)) {
          params = { client_id: payload.client_id };
          delete payload.client_id;
        }
        await automationApi.updateRule(id, payload, params);
        toast.success('Automation rule updated', { id: loader });
      } else {
        await automationApi.createRule(payload);
        toast.success('Automation rule created', { id: loader });
      }
      navigate('/automation?tab=rules');
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Error saving rule to API', { id: loader });
    }
  };

  const handleRunTest = async () => {
    let parsedPayload = {};
    try {
      parsedPayload = JSON.parse(testingPayload);
    } catch (err) {
      toast.error('Invalid sample payload JSON format');
      return;
    }

    const payload = {
      rule: {
        name: ruleName,
        trigger: { source: triggerSource, signal_types: triggerSignals, ...triggerOptions },
        conditions: conditionGroups.length > 0 ? {
          groups: conditionGroups.map(g => ({
            id: g.id,
            group_logic: g.groupLogic || 'AND',
            rules: g.rules.map(r => ({
              id: r.id,
              field: r.field,
              op: r.op,
              value: r.value,
              logic: r.logic || 'AND'
            }))
          }))
        } : null,
        actions,
      },
      asset_id: parseInt(testingAssetId) || null,
      sample_payload: parsedPayload
    };

    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await automationApi.testRule(payload);
      if (res.data.status) {
        setTestResult(res.data.data);
        toast.success('Dry run simulation complete!');
      }
    } catch (err) {
      // Fallback mock test result
      setTestResult({
        scope_matched: true,
        conditions_matched: conditions.length === 0 || conditions.every(c => c.value !== 'false'),
        would_fire: true,
        condition_trace: conditions.map(c => ({
          field: c.field,
          op: c.op,
          value: c.value,
          actual: c.value,
          result: true
        })),
        alert_preview: {
          title: `Alert: ${ruleName} on Test_Asset_01`,
          message: 'Automation rule matches signal conditions'
        },
        action_previews: actions.map(act => ({
          type: act.type,
          id: act.id,
          subject: 'ALERT: ' + ruleName,
          body: 'Signal meets conditions for ' + act.type
        }))
      });
      toast.success('Simulation executed (demo mode)');
    } finally {
      setTestRunning(false);
    }
  };

  const getActionConditionId = useCallback((act: { condition_id?: string }) => {
    let sourceNode = act.condition_id;
    if (!sourceNode || sourceNode === 'conditions') {
      sourceNode = 'condition-0';
    }
    if (sourceNode.startsWith('condition-')) {
      const condIdx = parseInt(sourceNode.split('-')[1]);
      if (condIdx >= conditions.length) {
        sourceNode = `condition-${conditions.length > 0 ? conditions.length - 1 : 0}`;
      }
    }
    return sourceNode;
  }, [conditions.length]);

  const displayedActions = useMemo(() => {
    if (selectedNodeId === 'trigger') {
      return actions;
    }
    if (selectedNodeId?.startsWith('condition-')) {
      return actions.filter(act => getActionConditionId(act) === selectedNodeId);
    }
    if (selectedNodeId === 'conditions') {
      if (conditions.length === 0) return actions;
      return actions.filter(act => getActionConditionId(act) === 'condition-0');
    }
    if (selectedNodeId && selectedNodeId !== 'trigger' && !selectedNodeId.startsWith('condition-')) {
      const selectedAction = actions.find(a => a.id === selectedNodeId);
      if (selectedAction) {
        const condId = getActionConditionId(selectedAction);
        return actions.filter(act => getActionConditionId(act) === condId);
      }
    }
    return actions;
  }, [selectedNodeId, actions, conditions.length, getActionConditionId]);

  const actionsListTitle = useMemo(() => {
    if (selectedNodeId === 'trigger') {
      return `Actions Flow List (${actions.length})`;
    }
    if (selectedNodeId?.startsWith('condition-')) {
      const idx = parseInt(selectedNodeId.split('-')[1]);
      return `Condition ${idx + 1} Actions (${displayedActions.length})`;
    }
    if (selectedNodeId === 'conditions') {
      return `Condition Actions (${displayedActions.length})`;
    }
    if (selectedNodeId && selectedNodeId !== 'trigger') {
      const selectedAction = actions.find(a => a.id === selectedNodeId);
      if (selectedAction) {
        const condId = getActionConditionId(selectedAction);
        if (condId.startsWith('condition-')) {
          const idx = parseInt(condId.split('-')[1]);
          return `Condition ${idx + 1} Actions (${displayedActions.length})`;
        }
      }
    }
    return `Actions Flow List (${displayedActions.length})`;
  }, [selectedNodeId, actions.length, displayedActions.length, getActionConditionId]);

  const availableActionTypes = useMemo(() => {
    const alertActionDef = {
      type: 'alert',
      label: 'Create Alert',
      key: 'alert',
      integration_type: 'alert',
      fields: [
        { name: 'title', key: 'title', label: 'Alert Title', type: 'text', required: true, placeholder: 'e.g. System Alert' },
        { name: 'severity', key: 'severity', label: 'Severity', type: 'select', values: [{ value: 'info', label: 'Info' }, { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }, { value: 'critical', label: 'Critical' }] },
        { name: 'message', key: 'message', label: 'Alert Message', type: 'template_text', placeholder: 'Alert details for {{record_key}}' }
      ]
    };

    const whatsappActionDef = {
      type: 'whatsapp',
      label: 'WhatsApp Message',
      key: 'whatsapp',
      integration_type: 'whatsapp',
      fields: [
        { name: 'phone_number', key: 'phone_number', label: 'Phone Number', type: 'text', required: true, placeholder: '+1234567890' },
        { name: 'template_name', key: 'template_name', label: 'Template Name', type: 'text', required: false, placeholder: 'e.g. alert_notification' },
        { name: 'template_language', key: 'template_language', label: 'Template Language', type: 'language', required: false, placeholder: 'Select Language' },
        { name: 'message', key: 'message', label: 'Message Text', type: 'template_text', placeholder: 'WhatsApp message content' }
      ]
    };

    let list: any[] = [];
    if (ruleEngineActionsCatalog?.actions?.length) {
      list = ruleEngineActionsCatalog.actions.map((act: any) => ({
        type: act.key || act.type,
        label: act.label,
        key: act.key || act.type,
        integration_type: act.integration_type,
        config_table: act.config_table,
        fields: (act.fields || act.config_fields || []).map((f: any) => ({
          name: f.key || f.name,
          key: f.key || f.name,
          label: f.label,
          type: f.type,
          required: f.required,
          placeholder: f.placeholder,
          api: f.api,
          depends_on: f.depends_on,
          depends_param: f.depends_param,
          option_label_key: f.option_label_key,
          option_value_key: f.option_value_key,
          send_as_object: f.send_as_object,
          description: f.description,
          values: f.values,
          options: f.options
        }))
      }));
    } else {
      list = (catalog?.action_types || []).map(act => ({
        type: act.type,
        label: act.label,
        key: act.type,
        integration_type: act.type,
        fields: (act.config_fields || []).map(f => ({
          name: f.name,
          key: f.name,
          label: f.label,
          type: f.type,
          required: f.required
        }))
      }));
    }

    // Ensure WhatsApp actions have template_language field available
    list.forEach(act => {
      if (act.type === 'whatsapp' || act.key === 'whatsapp') {
        const hasLang = act.fields?.some((f: any) => {
          const keyLower = (f.name || f.key || '').toLowerCase();
          return keyLower.includes('language') || keyLower.includes('lang');
        });
        if (!hasLang && act.fields) {
          act.fields.push({
            name: 'template_language',
            key: 'template_language',
            label: 'Template Language',
            type: 'language',
            required: false,
            placeholder: 'Select Language'
          });
        }
      }
    });

    if (!list.some(a => a.type === 'whatsapp' || a.key === 'whatsapp')) {
      list.push(whatsappActionDef);
    }

    if (!list.some(a => a.type === 'alert' || a.key === 'alert')) {
      list.unshift(alertActionDef);
    }
    return list;
  }, [ruleEngineActionsCatalog, catalog]);

  if (loading) {
    return (
      <div className="h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Get options for selected node panel configuration
  const currentTriggerDetails = catalog?.sources.find(s => s.name === triggerSource);
  const currentActionNode = selectedNodeId && selectedNodeId !== 'trigger' && selectedNodeId !== 'conditions' && !selectedNodeId.startsWith('condition-')
    ? actions.find(a => a.id === selectedNodeId)
    : null;
  const currentActionDetails = currentActionNode
    ? availableActionTypes.find((a: any) => a.type === currentActionNode.type)
    : null;

  const includeClients = triggerOptions.include_clients || [];
  const includeTags = triggerOptions.include_tags || [];
  const excludeTags = triggerOptions.exclude_tags || [];
  // const subnets = triggerOptions.subnets || [];
  // const deviceTypes = triggerOptions.device_types || [];
  // const platforms = triggerOptions.platforms || [];
  const uniqueTags = Array.from(new Set(lookups?.tags?.map((t: any) => t.name) || []))
    .filter(Boolean)
    .sort((a: any, b: any) => a.localeCompare(b));

  const renderTicketingDropdown = (field: any, actionNode: any) => {
    if (!ticketingDirectory) return null;
    const nameLower = field.name.toLowerCase();
    const labelLower = field.label.toLowerCase();

    // 1. Department dropdown
    if (nameLower.includes('depart') || labelLower.includes('depart')) {
      const options = (ticketingDirectory.departments || []).map((d: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(d.id) : d.name,
        label: d.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Department..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    // 2. User/Agent dropdown
    if (
      nameLower.includes('user') ||
      labelLower.includes('user') ||
      nameLower.includes('agent') ||
      labelLower.includes('agent') ||
      nameLower.includes('assignee') ||
      labelLower.includes('assignee') ||
      nameLower.includes('member') ||
      labelLower.includes('member') ||
      nameLower.includes('engineer') ||
      labelLower.includes('engineer')
    ) {
      const allUsers = [...(ticketingDirectory.users || []), ...(ticketingDirectory.internal_users || [])];
      const uniqueUsers: any[] = [];
      const seen = new Set();
      for (const u of allUsers) {
        const key = u.id || u.name;
        if (key && !seen.has(key)) {
          seen.add(key);
          uniqueUsers.push(u);
        }
      }

      const options = uniqueUsers.map((u: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(u.id) : u.name,
        label: u.email ? `${u.name} (${u.email})` : u.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select User..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    // 3. Group dropdown
    if (nameLower.includes('group') || labelLower.includes('group') || nameLower.includes('grop') || labelLower.includes('grop')) {
      const options = (ticketingDirectory.groups || []).map((g: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(g.id) : g.name,
        label: g.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Group..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    // 4. Priority dropdown
    if (nameLower.includes('priority') || labelLower.includes('priority')) {
      const options = (ticketingDirectory.priorities || []).map((p: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(p.id) : p.name,
        label: p.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Priority..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    // 5. Ticket Type dropdown
    if (nameLower.includes('type') || labelLower.includes('type')) {
      const options = (ticketingDirectory.ticket_types || []).map((t: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(t.id) : t.name,
        label: t.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Type..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    // 6. Ticket Status dropdown
    if (nameLower.includes('status') || labelLower.includes('status')) {
      const options = (ticketingDirectory.ticket_statuses || []).map((s: any) => ({
        value: (nameLower.includes('id') || labelLower.includes('id')) ? String(s.id) : s.name,
        label: s.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Status..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    return null;
  };

  const renderScriptDropdown = (field: any, actionNode: any) => {
    if (!lookups || !lookups.scripts) return null;
    const nameLower = field.name.toLowerCase();
    const labelLower = field.label.toLowerCase();

    if (
      (nameLower.includes('script') || labelLower.includes('script')) &&
      !nameLower.includes('timeout') && !labelLower.includes('timeout') &&
      !nameLower.includes('second') && !labelLower.includes('second') &&
      !nameLower.includes('time') && !labelLower.includes('time')
    ) {
      const options = (lookups.scripts || []).map((s: any) => ({
        value: (nameLower.includes('name')) ? s.name : String(s.id),
        label: s.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={String(actionNode.config[field.name] || '')}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Script..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    return null;
  };

  const renderOnCallDropdown = (field: any, actionNode: any) => {
    if (!lookups || !lookups.oncall_schedules) return null;
    const nameLower = field.name.toLowerCase();
    const labelLower = field.label.toLowerCase();

    if (
      nameLower.includes('oncall') || labelLower.includes('oncall') ||
      nameLower.includes('on_call') || labelLower.includes('on-call')
    ) {
      const options = (lookups.oncall_schedules || []).map((s: any) => ({
        value: (nameLower.includes('name')) ? s.name : String(s.id),
        label: s.name,
      }));
      return (
        <SingleSelectLocalSearchDropdown
          value={String(actionNode.config[field.name] || '')}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Schedule..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    return null;
  };

  const renderCheckpointDropdown = (field: any, actionNode: any) => {
    const nameLower = field.name.toLowerCase();
    const labelLower = field.label.toLowerCase();

    const isMatch = (nameLower.includes('checkpoint') || labelLower.includes('checkpoint') || nameLower.includes('wait') || labelLower.includes('wait')) &&
      !nameLower.includes('duration') && !labelLower.includes('duration') &&
      !nameLower.includes('time') && !labelLower.includes('time') &&
      !nameLower.includes('second') && !labelLower.includes('second') &&
      !nameLower.includes('minute') && !labelLower.includes('minute') &&
      !nameLower.includes('hour') && !labelLower.includes('hour') &&
      !nameLower.includes('interval') && !labelLower.includes('interval') &&
      !nameLower.includes('timeout') && !labelLower.includes('timeout');

    if (isMatch) {
      const currentIndex = actions.findIndex(a => a.id === actionNode.id);
      const options = actions
        .filter(a => a.id !== actionNode.id)
        .map(a => {
          const actIdx = actions.findIndex(item => item.id === a.id);
          const actLabel = catalog?.action_types.find(typeOpt => typeOpt.type === a.type)?.label || a.type;
          const isEarlier = actIdx < currentIndex;
          return {
            value: a.id,
            label: isEarlier
              ? `↑ earlier in chain - ${actLabel} (${a.id})`
              : `↓ later in chain - ${actLabel} (${a.id})`,
          };
        });

      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[field.name] || ''}
          options={options}
          onChange={val => handleActionConfigChange(actionNode.id, field.name, val || '')}
          disabled={isViewMode}
          className="w-full font-sans"
          placeholder="Select Action/Checkpoint..."
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    return null;
  };

  const renderLanguageDropdown = (field: any, actionNode: any) => {
    const nameLower = (field.name || field.key || '').toLowerCase();
    const labelLower = (field.label || '').toLowerCase();

    const isLangField =
      field.type === 'language' ||
      nameLower === 'template_language' ||
      nameLower === 'language' ||
      nameLower === 'template_lang' ||
      nameLower === 'lang' ||
      labelLower.includes('template language') ||
      labelLower === 'language';

    if (isLangField) {
      const fieldKey = field.name || field.key;
      return (
        <SingleSelectLocalSearchDropdown
          value={actionNode.config[fieldKey] || 'en'}
          options={WHATSAPP_LANGUAGES}
          onChange={val => handleActionConfigChange(actionNode.id, fieldKey, val || '')}
          disabled={isViewMode}
          placeholder="Select Language"
          buttonClassName="!bg-gray-950 !border-gray-800 !h-8 !py-1 !px-2.5 !text-xs !rounded"
        />
      );
    }

    return null;
  };

  return (
    <div className="h-screen bg-gray-950 text-gray-100 flex flex-col overflow-hidden font-sans">
      <style>{`
        .sidebar-compact, .sidebar-compact * {
          font-size: 10px !important;
        }
        .sidebar-compact label {
          font-size: 8px !important;
          line-height: 1 !important;
        }
        .sidebar-compact h4, .sidebar-compact h5, .sidebar-compact .tracking-widest {
          font-size: 9px !important;
        }
        .sidebar-compact input, 
        .sidebar-compact select, 
        .sidebar-compact textarea, 
        .sidebar-compact button:not(.p-1):not(.p-1.5) {
          font-size: 10px !important;
          padding-top: 3px !important;
          padding-bottom: 3px !important;
          padding-left: 6px !important;
          padding-right: 6px !important;
        }
        .sidebar-compact .p-4 {
          padding: 10px !important;
        }
        .sidebar-compact .p-3 {
          padding: 8px !important;
        }
        .sidebar-compact .p-2.5 {
          padding: 6px !important;
        }
        .sidebar-compact .space-y-4 > * + * {
          margin-top: 8px !important;
        }
        .sidebar-compact .space-y-3 > * + * {
          margin-top: 6px !important;
        }
        .sidebar-compact .space-y-2 > * + * {
          margin-top: 4px !important;
        }
        .sidebar-compact .space-y-1.5 > * + * {
          margin-top: 3px !important;
        }
        .sidebar-compact .rounded-xl {
          border-radius: 8px !important;
        }
        .sidebar-compact .rounded-lg {
          border-radius: 6px !important;
        }
      `}</style>
      {/* Editor Top Bar */}
      <div className="flex-none bg-gray-900 border-b border-gray-800 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => navigate('/automation?tab=rules')}
            className="p-1 hover:bg-gray-800 rounded-lg text-gray-200 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5 px-2">
              <span className="text-xs font-bold text-white max-w-[200px] truncate select-none leading-none">
                {ruleName || 'Enter automation rule name...'}
              </span>
              {!isTriggerConditionDisabled && (
                <button
                  type="button"
                  onClick={() => {
                    setTempRuleName(ruleName);
                    setTempAssetType(targetAssetType);
                    setTempRuleDescription(description);
                    setShowNameModal(true);
                  }}
                  className="p-1 hover:bg-gray-800 rounded text-gray-200 hover:text-white transition-colors cursor-pointer"
                  title="Edit Rule Details"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="text-[9px] text-gray-200 px-2 mt-0.5 font-semibold uppercase tracking-wider">
              ENGINE CANVAS EDITOR
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Quick Category Toggle Buttons */}
          <div className="flex bg-gray-950 border border-gray-800 rounded-lg p-0.5 mr-1">
            <button
              onClick={() => {
                if (isSidebarOpen && selectedNodeId === 'trigger') {
                  setIsSidebarOpen(false);
                } else {
                  setSelectedNodeId('trigger');
                  setRightPanelTab('config');
                  setIsSidebarOpen(true);
                }
              }}
              className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${isSidebarOpen && selectedNodeId === 'trigger'
                ? 'bg-orange-500 text-white'
                : 'text-gray-200 hover:text-white'
                }`}
            >
              Trigger
            </button>
            <button
              onClick={() => {
                const targetNode = 'conditions';
                const isCondActive = selectedNodeId === 'conditions' || selectedNodeId?.startsWith('condition-');
                if (isSidebarOpen && isCondActive) {
                  setIsSidebarOpen(false);
                } else {
                  setSelectedNodeId(targetNode);
                  setRightPanelTab('config');
                  setIsSidebarOpen(true);
                }
              }}
              className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${isSidebarOpen && (selectedNodeId === 'conditions' || selectedNodeId?.startsWith('condition-'))
                ? 'bg-indigo-500 text-white'
                : 'text-gray-200 hover:text-white'
                }`}
            >
              Condition
            </button>
            <button
              onClick={() => {
                const targetNode = actions.length > 0 ? actions[0].id : 'new-action';
                const isActActive = selectedNodeId && selectedNodeId !== 'trigger' && selectedNodeId !== 'conditions' && !selectedNodeId.startsWith('condition-');
                if (isSidebarOpen && isActActive) {
                  setIsSidebarOpen(false);
                } else {
                  setSelectedNodeId(targetNode);
                  setRightPanelTab('config');
                  setIsSidebarOpen(true);
                }
              }}
              className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${isSidebarOpen && selectedNodeId && selectedNodeId !== 'trigger' && selectedNodeId !== 'conditions' && !selectedNodeId.startsWith('condition-')
                ? 'bg-amber-500 text-white'
                : 'text-gray-200 hover:text-white'
                }`}
            >
              Action
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-200 font-semibold uppercase">SEVERITY:</span>
            <CustomSelect
              value={severity}
              onChange={val => setSeverity(val as 'info' | 'warning' | 'critical')}
              disabled={isTriggerConditionDisabled}
              options={[
                { value: 'info', label: 'Info' },
                { value: 'warning', label: 'Warning' },
                { value: 'critical', label: 'Critical' },
              ]}
            />
          </div>

          <label htmlFor="editorActive" className="flex items-center gap-1.5 cursor-pointer select-none group">
            <div
              className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isActive
                ? 'bg-orange-500 border-orange-500 text-white shadow-sm shadow-orange-500/30'
                : 'bg-gray-950 border-gray-700 group-hover:border-gray-500'
                } ${isTriggerConditionDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isActive && (
                <svg className="w-3 h-3 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <input
              type="checkbox"
              id="editorActive"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              disabled={isTriggerConditionDisabled}
              className="sr-only"
            />
            <span className="text-[10px] text-gray-200 font-semibold">ACTIVE</span>
          </label>

          {isViewMode ? (
            <div className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 text-gray-200 text-[10px] font-bold rounded-lg uppercase tracking-wider select-none">
              READ-ONLY MODE
            </div>
          ) : (
            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white text-[11px] font-bold rounded-lg transition-all shadow-md hover:shadow-orange-500/10 cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" />
              SAVE WORKFLOW
            </button>
          )}
        </div>
      </div>

      {/* Editor Body */}
      <div className="flex-1 flex overflow-hidden min-h-0">

        {/* React Flow Canvas (n8n node structure) */}
        <div className="flex-1 relative bg-gray-950 border-r border-gray-900">


          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onNodeDragStop={(_evt, node) => {
              setFlowPositions(prev => ({
                ...prev,
                [node.id]: node.position,
                ...(node.id === 'trigger' ? { trigger: node.position } : {}),
                ...(node.id === 'conditions' ? { conditions: node.position } : {})
              }));
            }}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.25, maxZoom: 1.0 }}
            minZoom={0.5}
            maxZoom={1.5}
          >
            <Controls className="!bg-gray-900 !border-gray-800 !text-white" />
          </ReactFlow>

          {nodes.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10 pointer-events-none">
              <div className="bg-gray-900/90 border border-gray-800 rounded-2xl p-6 max-w-md shadow-2xl backdrop-blur-md pointer-events-auto space-y-3">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto text-orange-400">
                  <Zap className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">Empty Canvas</h3>
                  <p className="text-xs text-gray-200">No nodes added yet. Select a trigger event, condition, or action to start building your automation workflow.</p>
                </div>
                <div className="pt-2 text-left space-y-1.5">
                  <label className="text-[10px] text-gray-200 font-bold uppercase tracking-wider block">Select Trigger Event Source</label>
                  <CustomSelect
                    value={triggerSource}
                    onChange={(val) => {
                      setTriggerSource(val);
                      setSelectedNodeId('trigger');
                      setRightPanelTab('config');
                      setIsSidebarOpen(true);
                    }}
                    disabled={isTriggerConditionDisabled}
                    placeholder="Select Trigger Collection..."
                    className="w-full"
                    options={(ruleEngineCollections?.collections || []).map((col: any) => ({
                      value: col.key,
                      label: col.label,
                      icon: ({ className }: { className?: string }) => getTriggerIcon(col.key, col.icon, className || "w-3.5 h-3.5 text-orange-400"),
                    }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Configurations, advanced and testing panel */}
        <div
          style={{ width: isSidebarOpen ? `${sidebarWidth}px` : '0px' }}
          className={`bg-gray-900 flex flex-col overflow-hidden relative sidebar-compact ${isResizingSidebar ? 'select-none transition-none' : 'transition-all duration-300 ease-in-out'
            } ${isSidebarOpen ? 'opacity-100 border-l border-gray-800' : 'opacity-0 border-l-transparent pointer-events-none'
            }`}
        >
          {/* Drag Handle on Left Edge */}
          {isSidebarOpen && (
            <div
              onMouseDown={handleMouseDownResize}
              className="absolute top-0 bottom-0 left-0 w-2 cursor-col-resize hover:bg-orange-500/50 active:bg-orange-500 transition-colors z-30 group"
              title="Drag to resize sidebar width"
            >
              <div className="absolute top-1/2 -translate-y-1/2 left-0.5 w-1 h-8 bg-gray-600 group-hover:bg-orange-400 rounded-full transition-colors" />
            </div>
          )}

          <div style={{ width: `${sidebarWidth}px` }} className="h-full flex flex-col flex-shrink-0 min-h-0">
            {/* Panel Selector Tabs */}
            <div className="flex border-b border-gray-800 bg-gray-950/20 flex-shrink-0 items-center justify-between pr-2">
              <div className="flex flex-1">
                {(['config'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setRightPanelTab(tab)}
                    className={`flex-1 py-2 text-center text-[10px] font-bold uppercase tracking-wider border-b-2 transition-colors ${rightPanelTab === tab
                      ? 'border-orange-500 text-white bg-gray-900/10'
                      : 'border-transparent text-gray-200 hover:text-white'
                      }`}
                  >
                    {tab === 'config' ? 'Node Config' : tab === 'advanced' ? 'Advanced' : 'Trace Test'}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setIsSidebarOpen(false)}
                className="p-1 hover:bg-gray-800 rounded-lg text-gray-200 hover:text-white transition-colors cursor-pointer ml-1.5"
                title="Hide Sidebar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Tab Views content container */}
            <div className="flex-1 overflow-y-auto custom-scroll p-2.5 pb-20 space-y-2.5">

              {/* NODE CONFIG TAB */}
              {rightPanelTab === 'config' && (
                <div className="space-y-3">

                  {/* Actions Flow List Roster */}
                  {selectedNodeId && selectedNodeId !== 'trigger' && selectedNodeId !== 'conditions' && !selectedNodeId.startsWith('condition-') && (
                    <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold text-white uppercase tracking-widest flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                          {actionsListTitle}
                        </h4>
                        {!isViewMode && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowActionSelector(prev => !prev)}
                              className="flex items-center gap-1 text-[11px] text-orange-400 font-bold hover:text-orange-300 cursor-pointer"
                            >
                              <Plus className="w-3.5 h-3.5" /> ADD ACTION
                            </button>

                            {showActionSelector && (
                              <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowActionSelector(false)} />
                                <div className="absolute right-0 top-6 z-50 w-56 bg-gray-950 border border-gray-800 rounded-lg shadow-xl py-1 custom-scroll max-h-56 overflow-y-auto">
                                  {availableActionTypes
                                    .filter((act: any) => !actions.some(a => isSameActionType(a.type, act.type)))
                                    .map((act: any) => (
                                      <button
                                        key={act.type}
                                        type="button"
                                        onClick={() => handleAddAction(act.type)}
                                        className="w-full px-3 py-2 text-left text-xs text-gray-200 hover:text-white hover:bg-gray-800/60 transition-colors flex items-center gap-2.5 cursor-pointer"
                                      >
                                        {getActionIcon(act.type, "w-4 h-4")}
                                        <span className="truncate font-medium">{act.label}</span>
                                      </button>
                                    ))}
                                  {availableActionTypes.filter((act: any) => !actions.some(a => isSameActionType(a.type, act.type))).length === 0 && (
                                    <div className="px-3 py-2 text-[11px] text-gray-400 italic text-center">
                                      All available actions added
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {displayedActions.length === 0 ? (
                        <div className="text-[11px] text-gray-200 italic py-2 px-1 text-center border border-dashed border-gray-800 rounded-lg">
                          No actions connected to this condition. Click + ADD ACTION above.
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {displayedActions.map((act) => (
                            <button
                              key={act.id}
                              type="button"
                              onClick={() => setSelectedNodeId(act.id)}
                              className={`text-[11px] px-2.5 py-1.5 rounded-lg border transition-all ${selectedNodeId === act.id
                                ? 'bg-amber-500/20 border-amber-500/70 text-amber-300 font-bold'
                                : 'bg-gray-950 border-gray-800 text-gray-200 hover:text-white'
                                }`}
                            >
                              {act.type.toUpperCase()} ({act.id})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedNodeId === 'trigger' && (
                    <div className="space-y-4">
                      <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 space-y-3">
                        <h4 className="text-xs font-bold text-orange-400 uppercase tracking-widest">Trigger Source Settings</h4>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Target Asset Type</label>
                          <CustomSelect
                            value={targetAssetType}
                            onChange={val => {
                              setTargetAssetType(val);
                              setTriggerOptions(prev => ({ ...prev, target_asset_type: val }));
                              fetchCatalogAndLookups(val);
                            }}
                            disabled={isTriggerConditionDisabled}
                            className="w-full"
                            options={assetTypeOptions}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Select Event Source (Collection)</label>
                          <CustomSelect
                            value={triggerSource}
                            onChange={val => {
                              setTriggerSource(val);
                              setTriggerSignals([]);
                              setTriggerOptions(_prev => ({ target_asset_type: targetAssetType }));
                            }}
                            disabled={isTriggerConditionDisabled}
                            className="w-full"
                            options={(ruleEngineCollections?.collections || []).map((col: any) => ({
                              value: col.key,
                              label: col.label,
                              icon: ({ className }: { className?: string }) => getTriggerIcon(col.key, col.icon, className || "w-3.5 h-3.5 text-orange-400"),
                            }))}
                          />
                        </div>


                      </div>

                      {/* Signal Types Checklist */}
                      {(() => {
                        const collectionsList = ruleEngineCollections?.collections || [];
                        const currCol = collectionsList.find((c: any) => c.key === triggerSource || c.db_table === triggerSource);
                        const sigList = currCol?.signal_types || currentTriggerDetails?.signal_types?.map((s: any) => s.value) || ['created', 'updated', 'deleted'];

                        if (sigList.length === 0) return null;
                        return (
                          <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-2">
                            <label className="text-[10px] text-gray-200 font-bold uppercase block mb-1">Signal Types (Check to listen)</label>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scroll pr-1">
                              {sigList.map((sig: string) => {
                                const checked = triggerSignals.includes(sig);
                                return (
                                  <label
                                    key={sig}
                                    htmlFor={`sig-${sig}`}
                                    className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all cursor-pointer select-none ${checked
                                      ? 'bg-orange-500/10 border-orange-500/40 text-orange-300 shadow-sm'
                                      : 'bg-gray-950/80 border-gray-800 hover:border-gray-700 text-gray-200'
                                      } ${isTriggerConditionDisabled ? 'opacity-75 cursor-not-allowed' : ''}`}
                                  >
                                    <span className="text-[11px] font-bold uppercase tracking-wider">{sig}</span>
                                    <div
                                      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${checked
                                        ? 'bg-orange-500 border-orange-500 text-white shadow-sm shadow-orange-500/30'
                                        : 'bg-gray-900 border-gray-700'
                                        }`}
                                    >
                                      {checked && (
                                        <svg className="w-3 h-3 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      )}
                                    </div>
                                    <input
                                      type="checkbox"
                                      id={`sig-${sig}`}
                                      checked={checked}
                                      disabled={isTriggerConditionDisabled}
                                      onChange={e => {
                                        if (e.target.checked) {
                                          setTriggerSignals(prev => [...prev, sig]);
                                        } else {
                                          setTriggerSignals(prev => prev.filter(v => v !== sig));
                                        }
                                      }}
                                      className="sr-only"
                                    />
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Source specific Extra Options dynamic fields */}
                      {currentTriggerDetails && currentTriggerDetails.options?.length > 0 && (
                        <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Source Parameters</label>
                          {currentTriggerDetails.options.map((opt) => (
                            <div key={opt.name} className="space-y-1">
                              <label className="text-[10px] text-gray-200 font-bold uppercase">{opt.label}</label>
                              {opt.type === 'number' ? (
                                <input
                                  type="number"
                                  value={triggerOptions[opt.name] ?? opt.default ?? ''}
                                  disabled={isTriggerConditionDisabled}
                                  onChange={e => setTriggerOptions(prev => ({ ...prev, [opt.name]: parseInt(e.target.value) }))}
                                  className="w-full bg-gray-950 border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={triggerOptions[opt.name] ?? opt.default ?? ''}
                                  disabled={isTriggerConditionDisabled}
                                  onChange={e => setTriggerOptions(prev => ({ ...prev, [opt.name]: e.target.value }))}
                                  className="w-full bg-gray-950 border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 disabled:opacity-75 disabled:cursor-not-allowed"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* SCOPE (OPTIONAL NARROWING) */}
                      <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                        <h4 className="text-[11px] font-bold text-white uppercase tracking-wider border-b border-gray-800 pb-2">
                          Scope (Optional Narrowing)
                        </h4>

                        {/* Include clients (Global Level Only) */}
                        {!isClientLevel && (
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-200 font-bold uppercase block">Include clients</label>
                            <CustomSelect
                              value=""
                              placeholder="+ add..."
                              disabled={isTriggerConditionDisabled}
                              onChange={(val) => {
                                const clientName = lookups?.clients.find((c: any) => String(c.id) === String(val))?.name || val;
                                handleAddScopeItem('include_clients', clientName);
                              }}
                              className="w-full mb-1"
                              options={
                                lookups?.clients.map((c: any) => ({
                                  value: String(c.id),
                                  label: c.name,
                                })) || []
                              }
                            />
                            {includeClients.length > 0 && (
                              <div className="flex flex-wrap gap-1 p-1 bg-gray-950 border border-gray-800 rounded-lg">
                                {includeClients.map((item: string) => (
                                  <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-855 rounded px-2 py-0.5 text-[10px] text-gray-200">
                                    {item}
                                    <button
                                      type="button"
                                      disabled={isTriggerConditionDisabled}
                                      onClick={() => handleRemoveScopeItem('include_clients', item)}
                                      className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Include tags */}
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Include tags</label>
                          <CustomSelect
                            value=""
                            placeholder="+ add..."
                            disabled={isTriggerConditionDisabled}
                            onChange={(val) => handleAddScopeItem('include_tags', val)}
                            className="w-full mb-1"
                            options={
                              uniqueTags.map((name) => ({
                                value: name,
                                label: name,
                              }))
                            }
                          />
                          {includeTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 p-1 bg-gray-950 border border-gray-800 rounded-lg">
                              {includeTags.map((item: string) => (
                                <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-850 rounded px-2 py-0.5 text-[10px] text-gray-200">
                                  {item}
                                  <button
                                    type="button"
                                    disabled={isTriggerConditionDisabled}
                                    onClick={() => handleRemoveScopeItem('include_tags', item)}
                                    className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>                      {/* Exclude tags */}
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Exclude tags</label>
                          <CustomSelect
                            value=""
                            placeholder="+ add..."
                            disabled={isTriggerConditionDisabled}
                            onChange={(val) => handleAddScopeItem('exclude_tags', val)}
                            className="w-full mb-1"
                            options={
                              uniqueTags.map((name) => ({
                                value: name,
                                label: name,
                              }))
                            }
                          />
                          {excludeTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 p-1 bg-gray-950 border border-gray-800 rounded-lg">
                              {excludeTags.map((item: string) => (
                                <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-850 rounded px-2 py-0.5 text-[10px] text-gray-200">
                                  {item}
                                  <button
                                    type="button"
                                    disabled={isTriggerConditionDisabled}
                                    onClick={() => handleRemoveScopeItem('exclude_tags', item)}
                                    className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Subnets (CIDR) */}
                        {/* <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Subnets (CIDR)</label>
                          <div className="w-full bg-gray-950 border border-gray-800 rounded-lg p-1.5 flex flex-wrap gap-1 items-center focus-within:border-orange-500 min-h-9">
                            {subnets.map((item: string) => (
                              <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-850 rounded px-2.5 py-0.5 text-[10px] text-gray-200">
                                {item}
                                <button
                                  type="button"
                                  disabled={isTriggerConditionDisabled}
                                  onClick={() => handleRemoveScopeItem('subnets', item)}
                                  className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              placeholder={subnets.length === 0 ? "10.0.0.0/24 + Enter" : ""}
                              disabled={isTriggerConditionDisabled}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.currentTarget.value.trim();
                                  if (val) {
                                    handleAddScopeItem('subnets', val);
                                    e.currentTarget.value = '';
                                  }
                                }
                              }}
                              className="flex-1 bg-transparent border-none outline-none text-xs text-white min-w-[80px]"
                            />
                          </div>
                        </div> */}

                        {/* Device types */}
                        {/* <div className="space-y-1.5">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Device types</label>
                          <CustomSelect
                            value=""
                            placeholder="add from inventory..."
                            disabled={isTriggerConditionDisabled}
                            onChange={(val) => handleAddScopeItem('device_types', val)}
                            className="w-full mb-1"
                            options={
                              lookups?.device_types.map((dt: any) => ({
                                value: dt,
                                label: dt,
                              })) || [
                                { value: 'switch', label: 'Switch' },
                                { value: 'camera', label: 'Camera' },
                                { value: 'firewall', label: 'Firewall' },
                                { value: 'ups', label: 'UPS' },
                                { value: 'printer', label: 'Printer' },
                              ]
                            }
                          />
                          <div className="w-full bg-gray-950 border border-gray-800 rounded-lg p-1.5 flex flex-wrap gap-1 items-center focus-within:border-orange-500 min-h-9">
                            {deviceTypes.map((item: string) => (
                              <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-850 rounded px-2.5 py-0.5 text-[10px] text-gray-200">
                                {item}
                                <button
                                  type="button"
                                  disabled={isTriggerConditionDisabled}
                                  onClick={() => handleRemoveScopeItem('device_types', item)}
                                  className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              placeholder={deviceTypes.length === 0 ? "switch, camera... + Enter" : ""}
                              disabled={isTriggerConditionDisabled}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.currentTarget.value.trim();
                                  if (val) {
                                    handleAddScopeItem('device_types', val);
                                    e.currentTarget.value = '';
                                  }
                                }
                              }}
                              className="flex-1 bg-transparent border-none outline-none text-xs text-white min-w-[80px]"
                            />
                          </div>
                        </div> */}

                        {/* Platforms */}
                        {/* <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase block">Platforms (windows / linux / macos / network)</label>
                          <div className="w-full bg-gray-950 border border-gray-800 rounded-lg p-1.5 flex flex-wrap gap-1 items-center focus-within:border-orange-500 min-h-9">
                            {platforms.map((item: string) => (
                              <span key={item} className="inline-flex items-center gap-1 bg-gray-900 border border-gray-850 rounded px-2.5 py-0.5 text-[10px] text-gray-200">
                                {item}
                                <button
                                  type="button"
                                  disabled={isTriggerConditionDisabled}
                                  onClick={() => handleRemoveScopeItem('platforms', item)}
                                  className="text-gray-200 hover:text-red-400 font-bold ml-0.5 select-none cursor-pointer"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              placeholder={platforms.length === 0 ? "add + Enter" : ""}
                              disabled={isTriggerConditionDisabled}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.currentTarget.value.trim();
                                  if (val) {
                                    handleAddScopeItem('platforms', val);
                                    e.currentTarget.value = '';
                                  }
                                }
                              }}
                              className="flex-1 bg-transparent border-none outline-none text-xs text-white min-w-[80px]"
                            />
                          </div>
                        </div> */}
                        {/* Cooldown & Resolution */}
                        {/* <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                          <h4 className="text-xs font-bold text-white uppercase tracking-wider border-b border-gray-800 pb-2">Cooldown & Resolution</h4>

                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-200 font-bold uppercase">Cooldown Minutes</label>
                            <input
                              type="number"
                              value={cooldown}
                              onChange={e => setCooldown(parseInt(e.target.value))}
                              disabled={isTriggerConditionDisabled}
                              className="w-full bg-gray-950 border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 disabled:opacity-75 disabled:cursor-not-allowed"
                            />
                            <span className="text-[9px] text-gray-200 block leading-tight">Suppresses repeating alarms on the same entity for this duration.</span>
                          </div>

                          <label
                            htmlFor="advAutoResolve"
                            className={`flex items-center gap-2.5 pt-2 cursor-pointer select-none ${isTriggerConditionDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div
                              className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${autoResolve
                                ? 'bg-orange-500 border-orange-500 text-white shadow-sm shadow-orange-500/30'
                                : 'bg-gray-950 border-gray-700 hover:border-gray-600'
                                }`}
                            >
                              {autoResolve && (
                                <svg className="w-3 h-3 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              id="advAutoResolve"
                              checked={autoResolve}
                              onChange={e => setAutoResolve(e.target.checked)}
                              disabled={isTriggerConditionDisabled}
                              className="sr-only"
                            />
                            <span className="text-xs text-gray-200 font-semibold select-none">
                              Enable Auto-Resolve on signal clear
                            </span>
                          </label>
                        </div> */}
                      </div>
                    </div>
                  )}

                  {(selectedNodeId === 'conditions' || selectedNodeId?.startsWith('condition-')) && (
                    <div className="space-y-4">
                      {/* Top Card: Collection Selection */}
                      <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4 space-y-3">
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Trigger Collection</label>
                          <CustomSelect
                            value={triggerSource}
                            onChange={val => {
                              setTriggerSource(val);
                              setTriggerSignals([]);
                              setTriggerOptions(_prev => ({ target_asset_type: targetAssetType }));
                            }}
                            disabled={isTriggerConditionDisabled}
                            className="w-full"
                            placeholder="Select Collection..."
                            options={(ruleEngineCollections?.collections || []).map((col: any) => ({
                              value: col.key,
                              label: col.label,
                              icon: ({ className }: { className?: string }) => getTriggerIcon(col.key, col.icon, className || "w-3.5 h-3.5 text-orange-400"),
                            }))}
                          />
                        </div>

                        <p className="text-[10px] text-gray-200 pt-1">
                          Define rules and nested group conditions for the incoming trigger events.
                        </p>
                      </div>

                      {/* Condition Tree Renderer */}
                      <div className="space-y-3">
                        <GroupNodeView
                          node={conditionTree}
                          path={[]}
                          collectionKey={triggerSource}
                          ruleEngineCollections={ruleEngineCollections}
                          ruleEngineOperators={ruleEngineOperators}
                          onChangeTree={onChangeTree}
                          onRemove={null}
                          depth={0}
                          isViewMode={isTriggerConditionDisabled}
                        />
                      </div>
                    </div>
                  )}


                  {currentActionNode && (
                    <div className="space-y-4">
                      <div className={`${currentActionNode.is_broken ? 'bg-red-500/10 border-red-500/40' : 'bg-amber-500/5 border-amber-500/20'} border rounded-xl p-4 space-y-3`}>
                        <div className="flex items-center justify-between">
                          <h4 className={`text-xs font-bold ${currentActionNode.is_broken ? 'text-red-400' : 'text-amber-400'} uppercase tracking-widest`}>
                            Action Nodes Settings {currentActionNode.is_broken && <span className="text-[10px] text-red-500 font-extrabold ml-1.5">(BROKEN)</span>}
                          </h4>
                          {!isViewMode && !isAlertAction(currentActionNode) && (
                            <button
                              type="button"
                              onClick={() => handleRemoveAction(currentActionNode.id)}
                              className="flex items-center gap-1 text-[10px] text-red-400 font-bold hover:text-red-300 cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> DELETE ACTION
                            </button>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Integration Type</label>
                          <CustomSelect
                            value={currentActionNode.type}
                            onChange={val => handleActionChange(currentActionNode.id, 'type', val)}
                            disabled={isViewMode || isAlertAction(currentActionNode)}
                            className="w-full font-sans"
                            options={availableActionTypes
                              .filter((act: any) => isSameActionType(act.type, currentActionNode.type) || !actions.some(a => a.id !== currentActionNode.id && isSameActionType(a.type, act.type)))
                              .map((act: any) => ({
                                value: act.type,
                                label: act.label,
                                icon: ({ className }: { className?: string }) => getActionIcon(act.type, className || "w-3.5 h-3.5"),
                              }))}
                          />
                        </div>
                      </div>

                      {/* Action execution properties */}
                      {conditions.length > 0 && (
                        <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-200 font-bold uppercase">Associated Condition</label>
                            <CustomSelect
                              value={currentActionNode.condition_id || 'condition-0'}
                              onChange={val => handleActionChange(currentActionNode.id, 'condition_id', val)}
                              disabled={isViewMode}
                              className="w-full font-sans"
                              options={conditions.map((cond, idx) => {
                                const triggerDetails = catalog?.sources.find(s => s.name === triggerSource);
                                const fieldLabel = triggerDetails?.fields.find(f => f.path === cond.field)?.label || cond.field || `Condition ${idx + 1}`;
                                return {
                                  value: `condition-${idx}`,
                                  label: `${idx + 1}. ${fieldLabel} (${cond.op})`,
                                };
                              })}
                            />
                          </div>
                        </div>
                      )}

                      {/* Action config shape parameters */}
                      {(() => {
                        const fields = currentActionDetails?.fields || currentActionDetails?.config_fields || [];
                        if (!fields || fields.length === 0) return null;
                        return (
                          <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl space-y-3">
                            <label className="text-[10px] text-gray-200 font-bold uppercase">Configuration Parameters</label>
                            {fields.map((field: any) => {
                              const fieldKey = field.key || field.name;
                              const showInsertVariableDropdown = field.insert_variable === true || (field.insert_variable !== false && field.type === 'template_text');
                              return (
                                <div key={fieldKey} className="space-y-1">
                                  {field.type !== 'boolean' && !showInsertVariableDropdown && (
                                    <label className="text-[10px] text-gray-200 font-bold uppercase block">
                                      {field.label} {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                  )}
                                  {field.type === 'boolean' ? (
                                    <label className="flex items-center gap-2 cursor-pointer select-none group">
                                      <div
                                        className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${currentActionNode.config[fieldKey]
                                          ? 'bg-orange-500 border-orange-500 text-white shadow-sm shadow-orange-500/30'
                                          : 'bg-gray-950 border-gray-700 group-hover:border-gray-500'
                                          } ${isViewMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        {Boolean(currentActionNode.config[fieldKey]) && (
                                          <svg className="w-3 h-3 stroke-[3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                          </svg>
                                        )}
                                      </div>
                                      <input
                                        type="checkbox"
                                        checked={Boolean(currentActionNode.config[fieldKey])}
                                        onChange={e => handleActionConfigChange(currentActionNode.id, fieldKey, e.target.checked)}
                                        disabled={isViewMode}
                                        className="sr-only"
                                      />
                                      <span className="text-[10px] text-gray-200 font-bold uppercase">
                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                      </span>
                                    </label>
                                  ) : field.type === 'channels' ? (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {['email', 'sms', 'whatsapp', 'telegram'].map((chan) => {
                                        const selectedList = currentActionNode.config[fieldKey] || [];
                                        const isSelected = selectedList.includes(chan);
                                        const handleToggle = () => {
                                          const nextList = isSelected
                                            ? selectedList.filter((c: string) => c !== chan)
                                            : [...selectedList, chan];
                                          handleActionConfigChange(currentActionNode.id, fieldKey, nextList);
                                        };
                                        return (
                                          <button
                                            key={chan}
                                            type="button"
                                            disabled={isViewMode}
                                            onClick={handleToggle}
                                            className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider transition-all duration-150 cursor-pointer disabled:cursor-not-allowed disabled:opacity-75 ${isSelected
                                              ? 'bg-amber-500/20 border-amber-500/60 text-amber-300 shadow-sm shadow-amber-500/5'
                                              : 'bg-gray-950 border-gray-800 text-gray-200 hover:text-white hover:bg-gray-800/40'
                                              }`}
                                          >
                                            {chan}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    (currentActionNode.type === 'ticket' && ticketingDirectory && renderTicketingDropdown(field, currentActionNode)) ||
                                    (currentActionNode.type === 'script' && lookups && renderScriptDropdown(field, currentActionNode)) ||
                                    (lookups && renderOnCallDropdown(field, currentActionNode)) ||
                                    renderCheckpointDropdown(field, currentActionNode) ||
                                    renderLanguageDropdown(field, currentActionNode) || (
                                      <DynamicActionField
                                        field={field}
                                        actionNode={currentActionNode}
                                        handleActionConfigChange={handleActionConfigChange}
                                        isViewMode={isViewMode}
                                        ruleEngineActionsCatalog={ruleEngineActionsCatalog}
                                        ruleEngineCollections={ruleEngineCollections}
                                        triggerSource={triggerSource}
                                        hasConditions={conditions.length > 0}
                                      />
                                    )
                                  )}
                                  {field.description && (
                                    <p className="text-[9px] text-gray-200 font-sans leading-tight mt-0.5">{field.description}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Add new Action Node tool */}
                  {(!selectedNodeId || selectedNodeId === 'new-action') && (
                    <div className="bg-gray-850 p-4 border border-gray-800 rounded-xl flex flex-col gap-2 items-center justify-center text-center h-32 text-gray-200">
                      <HelpCircle className="w-8 h-8 text-gray-200" />
                      <p className="text-xs">
                        {!selectedNodeId
                          ? "Select a node from the canvas or use the tabs above to begin configuration."
                          : "Click '+ ADD ACTION' above to create a new action."}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ADVANCED TAB
              {rightPanelTab === 'advanced' && (
                
              )} */}

              {/* TRACE TEST TAB */}
              {rightPanelTab === 'test' && (
                <div className="space-y-4">
                  <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 space-y-3">
                    <h4 className="text-xs font-bold text-orange-400 uppercase tracking-widest">Trace Testing simulation</h4>
                    <p className="text-[10px] text-gray-200 leading-normal">
                      Perform a dry-run test trace of the current editor rule configuration against test asset parameters. Does not commit data to database.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] text-gray-200 font-bold uppercase">Mock Asset ID Context</label>
                      <input
                        type="text"
                        value={testingAssetId}
                        onChange={e => setTestingAssetId(e.target.value)}
                        placeholder="e.g. 42"
                        className="w-full bg-gray-950 border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] text-gray-200 font-bold uppercase">Mock Signal Data Payload (JSON)</label>
                      <textarea
                        value={testingPayload}
                        onChange={e => setTestingPayload(e.target.value)}
                        className="w-full bg-gray-950 border border-gray-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-orange-500 font-mono text-[11px] h-32"
                      />
                    </div>

                    <button
                      onClick={handleRunTest}
                      disabled={testRunning}
                      className="w-full flex items-center justify-center gap-1.5 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-800 text-white rounded text-xs font-bold shadow-md transition-all duration-150 active:scale-95"
                    >
                      <Play className="w-4 h-4" />
                      {testRunning ? 'RUNNING TEST...' : 'RUN TEST SIMULATION'}
                    </button>
                  </div>

                  {/* Simulation Output trace rendering */}
                  {testResult && (
                    <div className="border-t border-gray-800 pt-4 space-y-4">
                      <h5 className="text-xs font-bold text-white uppercase tracking-wider">Simulation Output Trace</h5>

                      <div className="flex items-center gap-2 bg-gray-950 p-3 rounded-lg border border-gray-850">
                        {testResult.conditions_matched ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        )}
                        <div className="text-xs font-semibold">
                          {testResult.conditions_matched
                            ? 'Conditions Matched! Rule would trigger.'
                            : 'Conditions Failed. Rule would not execute actions.'
                          }
                        </div>
                      </div>

                      {/* Condition Trace evaluation breakdown */}
                      {testResult.condition_trace?.length > 0 && (
                        <div className="space-y-2">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Evaluated Leaves</label>
                          <div className="space-y-1.5">
                            {testResult.condition_trace.map((trace: any, i: number) => (
                              <div key={i} className="flex justify-between bg-gray-950/60 p-2.5 rounded border border-gray-850 text-xs">
                                <div>
                                  <span className="font-mono text-gray-200 text-[11px]">{trace.field}</span> {trace.op} <span className="font-mono text-gray-200 text-[11px]">{trace.value}</span>
                                </div>
                                <span className={`font-bold uppercase ${trace.result ? 'text-green-400' : 'text-red-400'}`}>
                                  {trace.result ? 'true' : 'false'} ({trace.actual})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rendered Template Alert preview */}
                      {testResult.alert_preview && (
                        <div className="bg-gray-950/30 p-3 rounded-lg border border-gray-850 space-y-1.5">
                          <label className="text-[10px] text-gray-200 font-bold uppercase">Generated Alert Preview</label>
                          <div className="text-xs font-bold text-white leading-tight">{testResult.alert_preview.title}</div>
                          <div className="text-xs text-gray-200">{testResult.alert_preview.message}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showNameModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <form
            onSubmit={handleNameSubmit}
            className="bg-gray-900 border border-gray-800 rounded-xl p-5 w-96 max-w-full shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150"
          >
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                {ruleName ? 'Edit Automation Rule' : 'Configure Automation Rule'}
              </h3>
              <p className="text-[10px] text-gray-200">
                {ruleName
                  ? 'Update rule name, target asset type, and optional description.'
                  : 'Enter a rule name, target asset type, and optional description before entering editor.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={tempRuleName}
                onChange={e => setTempRuleName(e.target.value)}
                placeholder="e.g. High CPU Load Notification, Port Status Monitor"
                autoFocus
                className="w-full bg-gray-950 border border-gray-800 focus:border-orange-500  /30 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">
                Target Asset Type <span className="text-red-500">*</span>
              </label>
              <CustomSelect
                value={tempAssetType}
                onChange={val => setTempAssetType(val)}
                options={assetTypeOptions}
                placeholder="Select Target Asset Type..."
                className="w-full font-sans"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-200 uppercase tracking-wider block">Description</label>
              <textarea
                value={tempRuleDescription}
                onChange={e => setTempRuleDescription(e.target.value)}
                placeholder="Brief description of what this automation rule does..."
                rows={3}
                className="w-full bg-gray-950 border border-gray-800 focus:border-orange-500  /30 rounded-lg px-3 py-2 text-xs text-white focus:outline-none resize-none"
              />
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-1">
              <button
                type="button"
                onClick={handleCancelNameModal}
                className="px-3 py-1.5 bg-gray-950 border border-gray-800 hover:bg-gray-800 text-gray-200 hover:text-white text-[11px] font-bold rounded-lg transition-colors cursor-pointer"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={!tempRuleName.trim() || !tempAssetType}
                className="px-3.5 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] font-bold rounded-lg transition-all shadow-md hover:shadow-orange-500/10 cursor-pointer"
              >
                CONTINUE
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AutomationEditor;
